// src/components/admin/command_center/tabs/EduEditorTab.jsx
// NSL Edu Editor v1.0 — Educational Video Exercise Engine

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useYouTubePlayer } from '../../../../minigames/useYouTubePlayer';
import SegmentTimePrecisionEditor from './SegmentTimePrecisionEditor';
import ManualExerciseEditor from './ManualExerciseEditor';
import CajaDeTranscripcion from '../../transcripcion/CajaDeTranscripcion';
import VistaDelEjercicio from './VistaDelEjercicio';
import { armaInforme } from './informeDeEjercicios';
import { hidrataVideoEdu, preparaParaGuardar, eligeElTexto } from './hidrataVideoEdu';
import AjusteDeTiempos from './AjusteDeTiempos';
import {
    fijaLosQueFaltan, mueveDisparo, puntosDeDisparo, relojFino,
} from './tiemposDeDisparo';
import {
    aplicaDesfase, desfaseQueCabe, diagnosticoDelDesfase,
} from './desfaseDeSegmentos';
import { trocea, cuentaDelTroceo, esTitulo } from './troceaTranscripcion';
import {
    plantillaParaOtraIA, leeEjercicios, colocaEjercicios,
} from './ejerciciosPegados';
import './EduEditorTab.css';

// ══════════════════════════════════════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════════════════════════════════════

const EXERCISE_TYPES = {
    truefalse: {
        label: 'Verdadero/Falso',
        emoji: '⚖️',
        color: '#818cf8',
        darkColor: 'rgba(129,140,248,0.15)',
        description: 'El alumno decide si una afirmación es verdadera o falsa.',
    },
    quiz: {
        label: 'Quiz',
        emoji: '🎯',
        color: '#34d399',
        darkColor: 'rgba(52,211,153,0.15)',
        description: 'Pregunta de comprensión con 4 opciones de respuesta.',
    },
    fill: {
        label: 'Completar',
        emoji: '✏️',
        color: '#fb923c',
        darkColor: 'rgba(251,146,60,0.15)',
        description: 'Rellena los espacios en blanco con las palabras correctas.',
    },
    wordselect: {
        label: 'Vocabulario',
        emoji: '🔤',
        color: '#60a5fa',
        darkColor: 'rgba(96,165,250,0.15)',
        description: 'Identifica palabras clave del fragmento.',
    },
    match: {
        label: 'Parejas',
        emoji: '🔗',
        color: '#f472b6',
        darkColor: 'rgba(244,114,182,0.15)',
        description: 'Conecta conceptos con sus definiciones.',
    },
    dragdrop: {
        label: 'Drag & Drop',
        emoji: '🧩',
        color: '#a78bfa',
        darkColor: 'rgba(167,139,250,0.15)',
        description: 'Arrastra palabras a los huecos correctos.',
    },
};

//  Los nombres que ve Rob, sueltos del resto de la configuración: los usan la
//  vista de revisión y el informe que se copia, y escribirlos dos veces es
//  garantizar que un día digan cosas distintas.
const ETIQUETAS_DE_TIPO = Object.fromEntries(
    Object.entries(EXERCISE_TYPES).map(([k, v]) => [k, v.label])
);

const LEVELS = [
    { value: 'A1', label: 'A1 — Principiante' },
    { value: 'A2', label: 'A2 — Elemental' },
    { value: 'B1', label: 'B1 — Intermedio' },
    { value: 'B2', label: 'B2 — Intermedio alto' },
    { value: 'C1', label: 'C1 — Avanzado' },
    { value: 'C2', label: 'C2 — Maestría' },
];

const LANGUAGES = [
    { value: 'English', label: '🇺🇸 English' },
    { value: 'Español', label: '🇲🇽 Español' },
];

const TOPICS = [
    { value: 'business', label: '💼 Business & Finance' },
    { value: 'tech', label: '💻 Technology' },
    { value: 'science', label: '🔬 Science' },
    { value: 'culture', label: '🎨 Culture & Arts' },
    { value: 'history', label: '📜 History' },
    { value: 'health', label: '🏥 Health & Medicine' },
    { value: 'environment', label: '🌿 Environment' },
    { value: 'politics', label: '🏛️ Politics & Society' },
    { value: 'sports', label: '⚽ Sports' },
    { value: 'general', label: '📚 General' },
];

//  Aquí vivía `SEG_LINES_DEFAULT = 6`: cuántas líneas se metían en cada
//  fragmento. Primero fue una constante escondida —42 líneas salían 7
//  fragmentos y nadie decía por qué—, y después una casilla para elegir el
//  número.
//
//  Rob, 07-10-2026: «eso es basura, yo quiero pasar los datos como son cuando
//  pegue lo real de YouTube... si son 30 cortes o 1000 cortes lo quiero así,
//  tal cual, no quiero agrupar nada».
//
//  Y la casilla era media respuesta: agrupar dejando elegir el número sigue
//  siendo agrupar. El transcript YA VIENE CORTADO —cada marca de tiempo es un
//  corte que alguien decidió— y volver a juntarlo para repartirlo de otra
//  manera es inventarse unos cortes encima de los que ya había.
//
//  Una marca de tiempo, un fragmento. Sin número y sin casilla.

// ══════════════════════════════════════════════════════════════════════
//  HELPERS & PARSERS (shared with SyncEditorTab)
// ══════════════════════════════════════════════════════════════════════

const extractId = (url) => {
    if (!url) return null;
    const m = url.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return m ? m[1] : url.trim();
};

const formatTime = (secs) => {
    if (secs === null || secs === undefined || isNaN(secs)) return '--:--';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
};

//  ── LA DÉCIMA, CUANDO LA HAY (14-10-2026) ─────────────────────────────
//  `formatTime` tira los decimales, y eso dejaba el desfase global INVISIBLE:
//  Rob pulsaba +.5, los 57 fragmentos se movían de verdad y las tarjetas seguían
//  diciendo `0:12 → 0:20`. Un botón que funciona y no se ve es un botón roto a
//  ojos de quien lo pulsa — y lo que vino a arreglar son medios segundos.
//
//  La décima sale SÓLO cuando existe: un transcript de segundos enteros se sigue
//  leyendo igual de limpio, y en cuanto algo se afina, se ve.
const formatTimeFino = (secs) => {
    if (secs === null || secs === undefined || isNaN(secs)) return '--:--';
    const d = Math.round((Math.abs(secs) % 1) * 10) % 10;
    return d === 0 ? formatTime(secs) : `${formatTime(secs)}.${d}`;
};

const parseAccessibilityTime = (str) => {
    const minSec = str.match(/^(\d+)\s+minute[s]?,\s*(\d+)\s+second[s]?$/i);
    if (minSec) return +minSec[1] * 60 + +minSec[2];
    const secOnly = str.match(/^(\d+)\s+second[s]?$/i);
    if (secOnly) return +secOnly[1];
    const minOnly = str.match(/^(\d+)\s+minute[s]?$/i);
    if (minOnly) return +minOnly[1] * 60;
    return null;
};

const isStageDirection = (text) => {
    const t = text.trim();
    const stageTerms = /\b(guitar|piano|drum|bass|interlude|intro|outro|instrumental|solo|fade|verse|chorus|bridge|soft|acoustic)\b/i;
    return /^\(.*\)$/.test(t) && stageTerms.test(t);
};

// ── Parse raw transcript into flat line objects ──────────────────────
const parseTranscript = (rawText) => {
    const normalized = rawText
        .replace(/(\d{1,2}:\d{2})(\d)/g, '$1\n$2')
        .replace(/((?:\d+\s+minutes?,\s*)?\d+\s+seconds?)([♪(A-Za-z])/g, '$1\n$2')
        .replace(/Sync to video time/gi, '');

    const rawArray = normalized.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    const result = [];
    let cTime = null;

    //  ── QUÉ LÍNEA TRAÍA SU PROPIA MARCA (07-10-2026) ────────────────────
    //  La primera línea de texto después de una marca de tiempo es la que la
    //  marca señala. Las siguientes, hasta la próxima marca, son continuación:
    //  llevan el mismo número porque no hay otro, pero NO es suyo.
    //
    //  Sin esta distinción, un título de capítulo —que en el panel de YouTube
    //  va entre dos marcas— se quedaba con el tiempo de la anterior, y el
    //  fragmento que abría empezaba diez segundos antes de su primera frase,
    //  encima del fragmento de al lado. Rob lo vio con dos empezando en 0:43.
    //
    //  El parser del servidor ya marcaba esto (`estimado`). Éste es el de
    //  respaldo —entra cuando aquél no contesta— y no lo hacía, así que los dos
    //  caminos daban cosas distintas. Un respaldo que se comporta distinto del
    //  original es un segundo programa que nadie mira.
    let primeraDeSuMarca = true;

    const pushText = (raw, time) => {
        if (isStageDirection(raw)) return;
        const text = raw.replace(/[♪♫]/g, '').replace(/\s+/g, ' ').trim();
        if (text.length > 1) {
            result.push({ text, time, estimado: !primeraDeSuMarca });
            primeraDeSuMarca = false;
        }
    };

    for (const line of rawArray) {
        // LRC format
        const lrcM = line.match(/\[(\d+):(\d+(?:\.\d+)?)\](.*)/);
        if (lrcM) {
            const t = +lrcM[1] * 60 + parseFloat(lrcM[2]);
            const text = lrcM[3].replace(/[♪♫]/g, '').trim();
            if (text) result.push({ text, time: t });
            continue;
        }
        // YT timestamp mm:ss
        if (/^\d{1,2}:\d{2}$/.test(line)) {
            const [min, sec] = line.split(':').map(Number);
            cTime = min * 60 + sec;
            primeraDeSuMarca = true;
            continue;
        }
        // YT timestamp hh:mm:ss
        if (/^\d{1,2}:\d{2}:\d{2}$/.test(line)) {
            const [h, min, sec] = line.split(':').map(Number);
            cTime = h * 3600 + min * 60 + sec;
            primeraDeSuMarca = true;
            continue;
        }
        // Accessibility time
        const accT = parseAccessibilityTime(line);
        if (accT !== null) {
            if (cTime !== null && Math.abs(accT - cTime) <= 2) cTime = accT;
            continue;
        }
        // Has music notes → split
        if (line.includes('♪') || line.includes('♫')) {
            const frags = line.split(/[♪♫]/).map(s => s.trim()).filter(s => s.length > 1);
            for (const frag of frags) pushText(frag, cTime);
        } else {
            pushText(line, cTime);
        }
    }

    return result;
};

// ── EL TROCEADO VIVE EN `troceaTranscripcion.js` ─────────────────────
//  Aquí había un `buildSegments` que cortaba cada seis líneas y le sumaba diez
//  segundos fijos a la última. Se fue a su propio fichero el 07-10-2026, con
//  número de líneas elegible, corte en los títulos del transcript y un final
//  calculado del propio transcript — y con bateria, porque equivocarse en esa
//  cuenta no se ve: salen fragmentos con su texto y sus tiempos y parecen bien.

// ══════════════════════════════════════════════════════════════════════
//  EXERCISE RENDERERS (for preview step)
// ══════════════════════════════════════════════════════════════════════

function ExercisePreview({ exercise, segmentText }) {
    const [selected, setSelected] = useState(null);
    const [answered, setAnswered] = useState(false);

    const handleAnswer = (val) => {
        if (answered) return;
        setSelected(val);
        setAnswered(true);
    };

    if (!exercise) {
        return (
            <div className="ee-preview-empty">
                <span>Sin ejercicio generado</span>
            </div>
        );
    }

    if (exercise.type === 'truefalse') {
        const isCorrect = answered && (selected === exercise.answer);
        return (
            <div className="ee-preview-exercise">
                <p className="ee-ex-statement">{exercise.statement}</p>
                <div className="ee-tf-btns">
                    {[true, false].map(val => (
                        <button
                            key={String(val)}
                            className={`ee-tf-btn ${answered && selected === val ? (isCorrect ? 'correct' : 'wrong') : ''} ${answered && val === exercise.answer ? 'correct-outline' : ''}`}
                            onClick={() => handleAnswer(val)}
                        >
                            {val ? '✅ Verdadero' : '❌ Falso'}
                        </button>
                    ))}
                </div>
                {answered && <p className="ee-ex-explanation">{exercise.explanation}</p>}
            </div>
        );
    }

    if (exercise.type === 'quiz') {
        return (
            <div className="ee-preview-exercise">
                <p className="ee-ex-question">{exercise.question}</p>
                <div className="ee-choices">
                    {exercise.choices?.map((choice, i) => {
                        const isCorrect = answered && i === exercise.correct;
                        const isWrong = answered && selected === i && i !== exercise.correct;
                        return (
                            <button
                                key={i}
                                className={`ee-choice-btn ${isCorrect ? 'correct' : ''} ${isWrong ? 'wrong' : ''}`}
                                onClick={() => handleAnswer(i)}
                            >
                                <span className="ee-choice-letter">{['A', 'B', 'C', 'D'][i]}</span>
                                {choice}
                            </button>
                        );
                    })}
                </div>
                {answered && <p className="ee-ex-explanation">{exercise.explanation}</p>}
            </div>
        );
    }

    if (exercise.type === 'fill') {
        const [inputs, setInputs] = useState(exercise.blanks?.map(() => null) || []);
        const [submitted, setSubmitted] = useState(false);

        const parts = exercise.template?.split('____') || [];
        return (
            <div className="ee-preview-exercise">
                <div className="ee-fill-template">
                    {parts.map((part, i) => (
                        <React.Fragment key={i}>
                            <span>{part}</span>
                            {i < parts.length - 1 && exercise.choices?.[i] && (
                                <select
                                    className={`ee-fill-select ${submitted ? (inputs[i] === exercise.blanks[i] ? 'correct' : 'wrong') : ''}`}
                                    value={inputs[i] || ''}
                                    onChange={e => {
                                        const n = [...inputs];
                                        n[i] = e.target.value;
                                        setInputs(n);
                                    }}
                                    disabled={submitted}
                                >
                                    <option value="">— elegir —</option>
                                    {exercise.choices[i].map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            )}
                        </React.Fragment>
                    ))}
                </div>
                {!submitted && (
                    <button className="ee-btn-check" onClick={() => setSubmitted(true)}>
                        Verificar
                    </button>
                )}
                {submitted && (
                    <p className="ee-ex-explanation">
                        ✅ Respuestas: {exercise.blanks?.join(', ')}
                    </p>
                )}
            </div>
        );
    }

    if (exercise.type === 'wordselect') {
        const words = exercise.text?.split(' ') || [];
        const [picked, setPicked] = useState([]);
        const [submitted, setSubmitted] = useState(false);

        const toggle = (word) => {
            if (submitted) return;
            setPicked(prev =>
                prev.includes(word) ? prev.filter(w => w !== word) : [...prev, word]
            );
        };

        return (
            <div className="ee-preview-exercise">
                <p className="ee-ex-hint">Selecciona las palabras clave del fragmento:</p>
                <div className="ee-wordselect">
                    {words.map((word, i) => {
                        const clean = word.replace(/[^a-zA-Z']/g, '').toLowerCase();
                        const isTarget = exercise.targetWords?.map(w => w.toLowerCase()).includes(clean);
                        const isPicked = picked.includes(word);
                        return (
                            <span
                                key={i}
                                className={`ee-ws-word ${isPicked ? 'picked' : ''} ${submitted && isTarget ? 'target' : ''} ${submitted && isPicked && !isTarget ? 'wrong-pick' : ''}`}
                                onClick={() => toggle(word)}
                            >
                                {word}
                            </span>
                        );
                    })}
                </div>
                {!submitted && (
                    <button className="ee-btn-check" onClick={() => setSubmitted(true)}>
                        Verificar
                    </button>
                )}
                {submitted && (
                    <p className="ee-ex-explanation">
                        🎯 Palabras clave: {exercise.targetWords?.join(', ')}
                    </p>
                )}
            </div>
        );
    }

    if (exercise.type === 'match') {
        const [selectedLeft, setSelectedLeft] = useState(null);
        const [selectedRight, setSelectedRight] = useState(null);
        const [matches, setMatches] = useState({});
        const [rights, setRights] = useState([]);
        
        useEffect(() => {
            if (exercise.pairs) {
                setRights([...exercise.pairs.map(p => p.right)].sort(() => Math.random() - 0.5));
            }
        }, [exercise.pairs]);

        const handleLeftClick = (idx) => {
            if (matches[idx] !== undefined) return;
            setSelectedLeft(idx);
            if (selectedRight !== null) {
                checkMatch(idx, selectedRight);
            }
        };

        const handleRightClick = (rightText) => {
            if (Object.values(matches).includes(rightText)) return;
            setSelectedRight(rightText);
            if (selectedLeft !== null) {
                checkMatch(selectedLeft, rightText);
            }
        };

        const checkMatch = (leftIdx, rightText) => {
            if (exercise.pairs[leftIdx].right === rightText) {
                setMatches(prev => ({ ...prev, [leftIdx]: rightText }));
                setSelectedLeft(null);
                setSelectedRight(null);
            } else {
                setTimeout(() => {
                    setSelectedLeft(null);
                    setSelectedRight(null);
                }, 400);
            }
        };

        const isFinished = exercise.pairs && Object.keys(matches).length === exercise.pairs.length;

        return (
            <div className="ee-preview-exercise">
                <p className="ee-ex-hint">Selecciona un concepto y luego su definición:</p>
                <div style={{ display: 'flex', gap: 20 }}>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {exercise.pairs?.map((pair, i) => {
                            const isMatched = matches[i] !== undefined;
                            const isSelected = selectedLeft === i;
                            return (
                                <button key={`l-${i}`} onClick={() => handleLeftClick(i)} 
                                        style={{ padding: 10, textAlign: 'left', borderRadius: 6, 
                                        background: isMatched ? 'rgba(16,185,129,0.15)' : isSelected ? 'var(--brand)' : 'var(--bg-elevated)', 
                                        border: `1px solid ${isMatched ? 'var(--green)' : isSelected ? 'var(--brand)' : 'var(--border)'}`,
                                        color: isMatched || isSelected ? '#fff' : 'var(--txt)', cursor: isMatched ? 'default' : 'pointer' }}>
                                    {pair.left} {isMatched && '✅'}
                                </button>
                            )
                        })}
                    </div>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {rights.map((rightText, i) => {
                            const isMatched = Object.values(matches).includes(rightText);
                            const isSelected = selectedRight === rightText;
                            return (
                                <button key={`r-${i}`} onClick={() => handleRightClick(rightText)}
                                        style={{ padding: 10, textAlign: 'left', borderRadius: 6,
                                        background: isMatched ? 'rgba(16,185,129,0.15)' : isSelected ? 'var(--brand)' : 'var(--bg-elevated)', 
                                        border: `1px solid ${isMatched ? 'var(--green)' : isSelected ? 'var(--brand)' : 'var(--border)'}`,
                                        color: isMatched || isSelected ? '#fff' : 'var(--txt)', cursor: isMatched ? 'default' : 'pointer' }}>
                                    {rightText} {isMatched && '✅'}
                                </button>
                            )
                        })}
                    </div>
                </div>
                {isFinished && <p className="ee-ex-explanation" style={{ marginTop: 15 }}>{exercise.explanation}</p>}
            </div>
        );
    }

    if (exercise.type === 'dragdrop') {
        const parts = exercise.template?.split('____') || [];
        const [inputs, setInputs] = useState(Array(Math.max(0, parts.length - 1)).fill(null));
        const [availableWords, setAvailableWords] = useState([]);
        const [submitted, setSubmitted] = useState(false);
        const [selectedWord, setSelectedWord] = useState(null);

        useEffect(() => {
            if (exercise.correctWords) {
                setAvailableWords([...exercise.correctWords, ...(exercise.distractors || [])].sort(() => Math.random() - 0.5));
            }
        }, [exercise.correctWords, exercise.distractors]);

        const handleBlankClick = (idx) => {
            if (submitted) return;
            if (selectedWord) {
                const newInputs = [...inputs];
                let newAvailable = availableWords.filter(w => w !== selectedWord);
                if (newInputs[idx]) {
                    newAvailable.push(newInputs[idx]);
                }
                newInputs[idx] = selectedWord;
                setInputs(newInputs);
                setAvailableWords(newAvailable);
                setSelectedWord(null);
            } else if (inputs[idx]) {
                setAvailableWords([...availableWords, inputs[idx]]);
                const newInputs = [...inputs];
                newInputs[idx] = null;
                setInputs(newInputs);
            }
        };

        const handleWordClick = (word) => {
            if (submitted) return;
            setSelectedWord(selectedWord === word ? null : word);
        };

        return (
            <div className="ee-preview-exercise">
                <p className="ee-ex-hint">Selecciona una palabra y haz clic en un hueco para colocarla:</p>
                <div className="ee-fill-template" style={{ lineHeight: 2.2 }}>
                    {parts.map((part, i) => (
                        <React.Fragment key={i}>
                            <span>{part}</span>
                            {i < parts.length - 1 && (
                                <button 
                                    onClick={() => handleBlankClick(i)}
                                    style={{ 
                                        display: 'inline-block', padding: '2px 8px', margin: '0 4px', 
                                        minWidth: 80, minHeight: 26, textAlign: 'center', verticalAlign: 'middle',
                                        background: inputs[i] ? 'var(--brand)' : 'rgba(255,255,255,0.05)',
                                        border: submitted 
                                            ? (inputs[i] === exercise.correctWords[i] ? '1px solid var(--green)' : '1px solid var(--red)')
                                            : '1px dashed var(--border)', 
                                        borderRadius: 4, color: inputs[i] ? '#fff' : 'transparent',
                                        cursor: submitted ? 'default' : 'pointer', fontSize: 14
                                    }}>
                                    {inputs[i] || '____'}
                                </button>
                            )}
                        </React.Fragment>
                    ))}
                </div>
                
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 15, padding: 10, background: 'rgba(0,0,0,0.2)', borderRadius: 8 }}>
                    {availableWords.map((word, i) => (
                        <button key={i} onClick={() => handleWordClick(word)}
                            style={{ 
                                background: selectedWord === word ? 'var(--brand)' : 'var(--bg-elevated)', 
                                padding: '6px 14px', borderRadius: 12, border: selectedWord === word ? '1px solid var(--brand)' : '1px solid var(--border)', 
                                fontSize: 13, cursor: submitted ? 'default' : 'pointer', color: selectedWord === word ? '#fff' : 'var(--txt)',
                                transition: 'all 0.15s'
                            }}>
                            {word}
                        </button>
                    ))}
                    {availableWords.length === 0 && <span style={{ color: 'var(--txt-dim)', fontStyle: 'italic', fontSize: 13 }}>No quedan palabras...</span>}
                </div>

                {!submitted && inputs.every(val => val !== null) && (
                    <button className="ee-btn-check" style={{ marginTop: 15 }} onClick={() => setSubmitted(true)}>
                        Verificar
                    </button>
                )}
                {submitted && (
                    <p className="ee-ex-explanation" style={{ marginTop: 15 }}>
                        {exercise.explanation}
                        <br/>
                        <span style={{ color: 'var(--txt-dim)' }}>Respuestas correctas: {exercise.correctWords?.join(', ')}</span>
                    </p>
                )}
            </div>
        );
    }

    return null;
}

// ══════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════

export default function EduEditorTab({ token, encargo, tomaEncargo }) {
    // ─ State ─────────────────────────────────────────────────────────
    const [step, setStep] = useState(1);

    //  ── MOVERSE ENTRE LOS CUATRO PASOS (06-10-2026) ───────────────────
    //  El stepper solo dejaba volver ATRÁS: `step > s.num`. Hacia adelante
    //  había que pasar por el botón de cada paso, y en el 1 ese botón es
    //  «Crear Segmentos», que REHACE el troceado. O sea que al reabrir un vídeo
    //  para corregir una falta de ortografía, bajar al paso 1 era un callejón
    //  sin salida cuya única puerta destruía el trabajo.
    //
    //  Ahora se guarda hasta dónde se ha llegado y se puede ir a cualquier paso
    //  ya visitado. No se abre nada que no se haya hecho: al paso 3 no se llega
    //  sin segmentos.
    const [pasoMaximo, setPasoMaximo] = useState(1);
    const vaAlPaso = useCallback((n) => {
        setStep(n);
        setPasoMaximo(m => Math.max(m, n));
    }, []);

    //  Qué vídeo se está corrigiendo, si es que se está corrigiendo alguno.
    //  `null` = uno nuevo, y entonces se publica en vez de guardarse encima.
    const [editandoId, setEditandoId] = useState(null);

    //  ── PASO 4: DÓNDE SALTA CADA EJERCICIO (06-10-2026) ───────────────
    //  Qué burbuja está elegida en la línea de tiempo, por «segId:exId».
    const [disparoElegido, setDisparoElegido] = useState(null);
    //  Si el vídeo está corriendo. Lo necesita el botón de play de esa pantalla:
    //  allí no hay más mando que ése, y un botón que no sabe en qué estado está
    //  enseña el icono contrario a lo que va a hacer.
    const [reproduciendo, setReproduciendo] = useState(false);

    //  ── PARAR EN LOS DISPAROS ─────────────────────────────────────────
    //  Rob: «si llega a un punto o a otro se detenga para medir exactamente
    //  dónde para y en qué frase».
    //
    //  Con esto puesto, al reproducir el vídeo se para SOLO en cada disparo,
    //  exactamente igual que le va a pasar al alumno. Es la única forma de ver
    //  si la pregunta cae donde tiene que caer: mirar un número no dice si la
    //  frase había terminado.
    const [pararEnDisparos, setPararEnDisparos] = useState(true);
    const [dondeParo, setDondeParo] = useState(null);   // { orden, clave, disparo }
    //  El segundo en el que se leyó el reloj la vez anterior. Un disparo se
    //  cruza cuando cae ENTRE esa lectura y ésta; comparar sólo con la de ahora
    //  pararía el vídeo cada vez que se lee, a cinco veces por segundo.
    const tiempoAnteriorRef = useRef(0);
    //  La última parada, para no volver a pararse en ella nada más seguir: tras
    //  el `seekTo` el reloj puede devolver una décima menos y volvería a cruzar
    //  el mismo punto una y otra vez. Se suelta en cuanto el vídeo la deja
    //  medio segundo atrás.
    const ultimaParadaRef = useRef(null);

    // Step 1 — Metadata
    const [videoId, setVideoId] = useState('');
    const [title, setTitle] = useState('');
    const [channel, setChannel] = useState('');
    const [topic, setTopic] = useState('general');
    const [language, setLanguage] = useState('English');
    const [level, setLevel] = useState('B1');
    const [rawTranscript, setRawTranscript] = useState('');

    // Step 2 — Segments
    const [segments, setSegments] = useState([]);
    //  Las líneas tal y como las leyó el parser. Se guardan para poder
    //  enseñar la cuenta —cuántas se leyeron y cuántos fragmentos salieron—
    //  sin volver a pedírselas al servidor.
    const [lineasLeidas, setLineasLeidas] = useState([]);

    //  ── OÍR UN FRAGMENTO SUELTO (07-10-2026) ──────────────────────────
    //  Rob: «si doy clic en alguno de ellos se debe reproducir el vídeo acorde
    //  a cada segmento para medir la precisión de que realmente entre en el
    //  momento exacto y salga cuando tiene que salir».
    //
    //  Guarda el fragmento que se está oyendo y DÓNDE tiene que parar. Antes
    //  «saltar a este momento» ponía el vídeo medio segundo antes y lo dejaba
    //  correr: servía para situarse, no para medir. Para medir hace falta que
    //  empiece donde dice y pare donde dice.
    const [oyendoFragmento, setOyendoFragmento] = useState(null);  // { id, fin }
    const oyendoRef = useRef(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [activeSegIdx, setActiveSegIdx] = useState(0);
    const [deleteRange, setDeleteRange] = useState('');

    //  ── MOVER TODOS LOS FRAGMENTOS A LA VEZ (14-10-2026) ──────────────
    //  Rob: «a veces todos tienen un desfase pequeño de .5 s y tengo que abrir
    //  todos para ajustar esos segundos y me quita mucho tiempo... siempre me
    //  pasa al terminar las oraciones».
    //
    //  Cuánto se ha movido el vídeo entero desde que se troceó. Se guarda para
    //  poder DESHACERLO de un clic: un botón que mueve 57 fragmentos sin vuelta
    //  atrás es el que más trabajo puede estropear de una pulsación.
    const [desfaseGlobal, setDesfaseGlobal] = useState(0);
    //  El desfase que no cupo entero y está esperando un sí. Ver
    //  `mueveTodosLosFragmentos`: hacia adelante se pregunta, no se prohíbe.
    const [confirmaDesfase, setConfirmaDesfase] = useState(null);

    //  ── EL TEXTO EN PANTALLA QUE YA TENÍA EL VÍDEO ────────────────────
    //  Se guarda tal cual se cargó para devolverlo al guardar: un `PUT`
    //  sustituye el `edu_data` entero, así que lo que no vuelva, se borra.
    const [captionsCargadas, setCaptionsCargadas] = useState([]);

    // Step 3 — Generation
    const [generatingAll, setGeneratingAll] = useState(false);
    const [genProgress, setGenProgress] = useState({ done: 0, total: 0 });

    // Precision editor — which segments have it open
    const [openPrecision, setOpenPrecision] = useState(new Set());
    // Manual exercise editor — which segment is being edited
    const [editingExKey, setEditingExKey] = useState(null); // { segId, exId } | null

    //  ── REVISAR LO QUE ESCRIBIÓ LA IA (05-10-2026) ────────────────────
    //  Qué ejercicios están abiertos en el paso 3, por «segId:exId». Es un
    //  conjunto y no un solo abierto a la vez a propósito: revisar es comparar
    //  dos ejercicios del mismo segmento, y cerrar el anterior al abrir el
    //  siguiente obliga a ir y volver.
    const [ejerciciosAbiertos, setEjerciciosAbiertos] = useState(() => new Set());
    //  El texto del informe cuando el navegador NO deja copiar solo. Ver
    //  `copiaElInforme`.
    const [informeAMano, setInformeAMano] = useState('');

    //  ── TODOS LOS EJERCICIOS DE UNA PEGADA (07-10-2026) ───────────────
    //  Rob: «¿se puede crear un botón con el formato esperado... para pasar
    //  todos los ejercicios que necesito de una sola vez, en vez de estar
    //  pegando y copiando cada uno, que me lleva mucho tiempo y a veces cometo
    //  errores?».
    const [pegando, setPegando] = useState(false);
    const [textoPegado, setTextoPegado] = useState('');
    //  Lo que se leyó, ANTES de tocar nada. Primero se enseña lo que va a
    //  pasar y después se hace: pegar quinientas líneas y que la pantalla
    //  cambie sola no deja revisar nada.
    const [lectura, setLectura] = useState(null);
    const [modoPegado, setModoPegado] = useState('añadir');
    const cajaDelInforme = useRef(null);
    // Video total duration (grabbed from YT player for mini-timeline)
    const [videoDuration, setVideoDuration] = useState(0);

    // UI
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState({ text: '', type: '' });

    const { playerRef, createPlayer, destroyPlayer } = useYouTubePlayer();
    const ytContainer = useRef(null);
    const segListRef = useRef(null);

    // ─ Toast ──────────────────────────────────────────────────────────
    //
    //  ── EL AVISO QUE SE BORRABA SOLO (14-10-2026) ─────────────────────
    //  Cada aviso dejaba su propio temporizador de 3,5 s y nadie cancelaba el
    //  anterior. Dos avisos seguidos —que es lo normal cuando se pulsa dos veces
    //  el mismo mando— y el temporizador del PRIMERO borraba al SEGUNDO al poco
    //  de aparecer: el aviso que explicaba por qué no se podía mover el vídeo
    //  parpadeaba y se iba.
    //
    //  Salió midiendo el desfase global: dos clics en −1s y el segundo, el que
    //  dice «no cabe», no llegaba a leerse. Un programa que frena y no alcanza a
    //  decir por qué es el castigo del editor de canciones otra vez.
    const relojDelAviso = useRef(null);
    const showToast = (text, type = 'info') => {
        if (relojDelAviso.current) clearTimeout(relojDelAviso.current);
        setToast({ text, type });
        relojDelAviso.current = setTimeout(() => {
            relojDelAviso.current = null;
            setToast({ text: '', type: '' });
        }, 3500);
    };

    // ─ Auth token ─────────────────────────────────────────────────────
    const authToken = token || localStorage.getItem('token');

    // ─ Player ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (step >= 2 && videoId) {
            createPlayer('edu-yt-player', extractId(videoId));
        }
        return () => destroyPlayer();
    }, [step, videoId, createPlayer, destroyPlayer]);

    // Real-time tracker
    useEffect(() => {
        if (step < 2) return;
        const timer = setInterval(() => {
            if (!playerRef.current) return;
            try {
                const t = playerRef.current.getCurrentTime();
                setCurrentTime(t);

                if (!videoDuration) {
                    const dur = playerRef.current.getDuration?.() ?? 0;
                    if (dur > 0) setVideoDuration(dur);
                }

                const estado = playerRef.current.getPlayerState();
                setReproduciendo(estado === 1 || estado === 3);

                //  ── EL FINAL DE UN FRAGMENTO SUELTO ───────────────────
                //  Se para EN el final, no donde haya llegado el reloj: entre
                //  dos lecturas pasan 200 ms, y lo que se viene a medir aquí
                //  es justo si entra y sale donde dice.
                const oyendo = oyendoRef.current;
                if (oyendo && estado === 1 && t >= oyendo.fin) {
                    try {
                        playerRef.current.pauseVideo();
                        playerRef.current.seekTo(oyendo.fin, true);
                    } catch (_) { }
                    oyendoRef.current = null;
                    setOyendoFragmento(null);
                    setCurrentTime(oyendo.fin);
                    return;
                }

                //  ── LA PARADA AUTOMÁTICA (paso 4) ─────────────────────
                if (step === 4 && pararEnDisparos && estado === 1) {
                    const antes = tiempoAnteriorRef.current;
                    const ultima = ultimaParadaRef.current;
                    if (ultima && t > ultima.disparo + 0.5) ultimaParadaRef.current = null;
                    const cruzado = puntosDeDisparo(segments).find(
                        pt => pt.disparo > antes && pt.disparo <= t
                            && pt.clave !== ultimaParadaRef.current?.clave);
                    if (cruzado) {
                        try {
                            playerRef.current.pauseVideo();
                            //  Se para EN el disparo, no donde haya llegado el
                            //  reloj: entre dos lecturas pasan 200 ms, y parar
                            //  dos décimas tarde es justo el error que se viene
                            //  a corregir.
                            playerRef.current.seekTo(cruzado.disparo, true);
                        } catch (_) { }
                        ultimaParadaRef.current = { clave: cruzado.clave, disparo: cruzado.disparo };
                        setDisparoElegido(cruzado.clave);
                        setDondeParo({ orden: cruzado.orden, clave: cruzado.clave,
                                       disparo: cruzado.disparo });
                        setCurrentTime(cruzado.disparo);
                        tiempoAnteriorRef.current = cruzado.disparo;
                        return;
                    }
                }
                tiempoAnteriorRef.current = t;

                if (estado === 1) {
                    let best = 0;
                    for (let i = 0; i < segments.length; i++) {
                        if (segments[i].startTime <= t) best = i;
                    }
                    setActiveSegIdx(best);
                }
            } catch (_) { }
        }, 200);
        return () => clearInterval(timer);
    }, [step, playerRef, segments, pararEnDisparos]);

    // Scroll active segment into view
    useEffect(() => {
        if (!segListRef.current) return;
        const el = segListRef.current.querySelectorAll('.ee-seg-card')[activeSegIdx];
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [activeSegIdx]);

    // ─ Parse & build segments ─────────────────────────────────────────
    //  ── EL MISMO PARSER QUE LAS CANCIONES (30-09-2026) ──────────────────
    //  Aquí se usaba `parseTranscript`, un parser escrito en JavaScript sólo
    //  para esta pantalla. El Sync Editor tenía otro, en Python. Dos parsers
    //  para el mismo trabajo se separan solos, y el que menos se mira es el que
    //  empieza a mentir: éste no sabía de .srt, ni de .vtt, ni de milisegundos,
    //  y pegaba en una línea los versos que vienen juntos en el panel.
    //
    //  Ahora los dos editores entran por el del servidor. Si la llamada falla
    //  —sin red, sesión caducada— se cae al de siempre en vez de dejar a nadie
    //  sin poder trabajar: peor parser es mejor que ningún parser.
    const [informeTranscript, setInformeTranscript] = useState(null);

    // ══════════════════════════════════════════════════════════════════
    //  CORREGIR UN VÍDEO YA GUARDADO
    // ══════════════════════════════════════════════════════════════════

    //  Llega desde «Videos Edu» con el vídeo entero. Se vuelca en el editor y
    //  se aterriza en el paso 3, que es donde están los ejercicios —lo que se
    //  viene a corregir— y desde donde se puede ir a cualquier otro.
    useEffect(() => {
        if (!encargo?.video) return;
        const d = hidrataVideoEdu(encargo.video);
        setEditandoId(d.id);
        setVideoId(d.videoId);
        setTitle(d.title);
        setChannel(d.channel);
        setTopic(d.topic);
        setLanguage(d.language);
        setLevel(d.level);
        setRawTranscript(d.rawTranscript);
        setSegments(d.segments);
        setActiveSegIdx(0);
        setDesfaseGlobal(0);
        setConfirmaDesfase(null);
        setCaptionsCargadas(d.captions || []);
        setEjerciciosAbiertos(new Set());
        setCambiandoTipo(new Set());
        setEditingExKey(null);
        setStep(3);
        setPasoMaximo(5);   // ya está todo hecho: se puede ir a cualquier paso
        showToast(
            `✏️ «${d.title}» abierto para corregir — ${d.segmentosLeidos} segmentos `
            + `y ${d.ejerciciosLeidos} ejercicios`,
            'success',
        );
        tomaEncargo?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [encargo]);

    const parseAndSegment = async () => {
        //  Esto REHACE los segmentos desde cero. Con un vídeo ya preparado
        //  delante, eso se lleva por delante todos los ejercicios escritos —y
        //  es el único botón que avanza desde el paso 1, así que se pulsa sin
        //  querer al bajar a corregir el título.
        const conTrabajo = segments.reduce(
            (n, sg) => n + sg.exercises.filter(e => e.status === 'ready').length, 0);
        if (conTrabajo > 0) {
            const ok = window.confirm(
                `Volver a crear los segmentos los rehace desde cero.\n\n`
                + `Se perderían los ${conTrabajo} ejercicios ya escritos de este vídeo `
                + `y habría que generarlos otra vez.\n\n`
                + `¿Seguro? Si sólo querías cambiar el título o el nivel, cámbialo y `
                + `vuelve al paso 3 desde arriba: lo de aquí no hace falta tocarlo.`
            );
            if (!ok) return;
        }
        let lines = [];
        let informe = null;
        try {
            const res = await fetch('/api/admin/edu-editor/parse-transcript', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
                body: JSON.stringify({ raw_text: rawTranscript }),
            });
            if (!res.ok) throw new Error('parse');
            const data = await res.json();
            //  Se queda TODO lo que manda el servidor. Antes se copiaban sólo
            //  `text` y `time`, y por el camino se perdía `estimado` —si el
            //  tiempo venía dado o lo dedujo el parser—, que es justo lo que
            //  distingue una línea hablada de un título de capítulo.
            lines = (data.lines || []).map(l => ({
                text: l.text, time: l.time, time_ms: l.time_ms, estimado: l.estimado,
                //  Y si el servidor dice que es un rótulo de sección, se cree:
                //  él ve el transcript entero y ya lo separó del texto hablado.
                rotulo: l.rotulo === true,
            }));
            informe = data.informe || null;
        } catch {
            lines = parseTranscript(rawTranscript);
            informe = null;
        }
        setInformeTranscript(informe);

        if (lines.length === 0) {
            showToast('⚠️ No se encontró texto válido en el transcript.', 'error');
            return;
        }
        const segs = trocea(lines);
        if (segs.length === 0) {
            showToast('⚠️ No se pudieron crear segmentos.', 'error');
            return;
        }
        setLineasLeidas(lines);
        setSegments(segs);
        setActiveSegIdx(0);
        setDesfaseGlobal(0);
        setConfirmaDesfase(null);
        vaAlPaso(2);
        //  La cuenta, dicha. Un programa que hace algo con los datos y no lo
        //  cuenta, desde fuera no se distingue de uno roto.
        const { titulos } = cuentaDelTroceo(lines);
        showToast(
            `✅ ${lines.length} líneas → ${segs.length} fragmentos, uno por marca de tiempo`
            + `${titulos ? ` · ${titulos} títulos de capítulo van con el suyo` : ''}.`,
            'success',
        );
    };

    //  ── MOVER EL VÍDEO ENTERO DE UN CLIC (14-10-2026) ─────────────────
    //  Rob: «poner lo mismo −1s −.5 −.1 │ +.1 +.5 +1s pero aquí la magia: si
    //  activo uno de esos botones se aplica a TODOS en un solo clic».
    //
    //  La cuenta se hace FUERA del `setSegments`. React llama al actualizador
    //  dos veces en desarrollo y no lo corre en el momento: calcular ahí dentro
    //  cuánto cabe y cuántos se movieron ya salió mal tres veces en el editor de
    //  canciones —el aviso contaba el doble o contaba cero—.
    //  Lo que de verdad mueve, una vez decidido cuánto. Se llama desde el botón
    //  y desde el «hacerlo igual» de la confirmación, y es el único sitio donde
    //  se tocan los segmentos: dos caminos distintos para lo mismo es como se
    //  acaba teniendo dos comportamientos distintos.
    const aplicaElDesfase = (cuanto, motivo = null) => {
        const { segments: nuevos, fragmentos, disparos, escritos } = aplicaDesfase(segments, cuanto);
        setSegments(nuevos);
        setDesfaseGlobal(Math.round((desfaseGlobal + cuanto) * 10) / 10);
        setConfirmaDesfase(null);

        //  Se nombra lo que se tocó. 99 fragmentos y sus preguntas moviéndose a
        //  la vez sin un aviso que lo diga no se distingue de un accidente.
        const signo = cuanto > 0 ? '+' : '';
        showToast(
            `🎚️ ${signo}${cuanto} s a ${fragmentos} fragmento${fragmentos === 1 ? '' : 's'}`
            + (disparos ? ` y a ${disparos} pregunta${disparos === 1 ? '' : 's'}` : '')
            + (escritos ? ` (${escritos} sin momento propio quedaron fijadas)` : '')
            + (motivo ? ` · ${motivo}` : ''),
            motivo ? 'warning' : 'success',
        );
    };

    const mueveTodosLosFragmentos = (delta) => {
        const diag = diagnosticoDelDesfase(segments, delta, videoDuration);

        //  Cabe entero: no hay nada que preguntar.
        if (diag.entero) { aplicaElDesfase(diag.cabe); return; }

        //  ── HACIA ADELANTE SE PREGUNTA, NO SE PROHÍBE (14-10-2026) ────
        //  Rob: «le di al +.5 y me marcó errores, lo cual es correcto... pero
        //  por eso ese botón increíble dejó de funcionar. Lo ideal es que el
        //  sistema sea más inteligente y me avise, yo confirmo si hacerlo de
        //  todos modos».
        //
        //  Los dos topes no son iguales. Por abajo no se puede pasar: un
        //  segundo negativo no existe. Por el final sí: una marca pasada del
        //  final se deshace moviendo para atrás. Y en su vídeo ya estaban fuera
        //  ANTES de tocar nada —99 fragmentos que acaban en el 5:21 de un vídeo
        //  de 2:59—, así que el tope no defendía nada: sólo dejaba el botón
        //  muerto justo cuando más falta hacía.
        if (diag.sePuedeInsistir) { setConfirmaDesfase(diag); return; }

        //  Hacia atrás: se mueve lo máximo que cabe, de una pieza, y se dice.
        //  Si no cabe ni una décima, no hay nada que hacer y también se dice.
        if (!diag.cabe) {
            showToast(`⚠️ No se puede mover ${delta > 0 ? '+' : ''}${delta} s: `
                + `${diag.motivo || 'no hay fragmentos con tiempo.'}`, 'error');
            setConfirmaDesfase(null);
            return;
        }
        aplicaElDesfase(diag.cabe, `sólo cupo ${diag.cabe}: ${diag.motivo}`);
    };

    //  Volver a donde estaba el vídeo antes de todos los desfases. Se mueve de
    //  una pieza, así que la vuelta es exacta al décimo.
    const quitaElDesfaseGlobal = () => {
        if (!desfaseGlobal) return;
        const cabe = desfaseQueCabe(segments, -desfaseGlobal, videoDuration);
        if (!cabe) { showToast('⚠️ No hay desfase que quitar.', 'error'); return; }
        const { segments: nuevos, fragmentos } = aplicaDesfase(segments, cabe);
        setSegments(nuevos);
        setDesfaseGlobal(Math.round((desfaseGlobal + cabe) * 10) / 10);
        setConfirmaDesfase(null);
        showToast(
            `↩ Desfase quitado: ${fragmentos} fragmento${fragmentos === 1 ? '' : 's'} `
            + 'de vuelta donde estaban.',
            'success',
        );
    };

    // ─ Add a new exercise slot to a segment ─────────────────────────
    const addExerciseToSeg = (segId) => {
        setSegments(prev => prev.map(s => {
            if (s.id !== segId) return s;
            const newEx = { id: Date.now() + Math.random(), type: 'quiz', data: null, status: 'pending' };
            return { ...s, exercises: [...s.exercises, newEx] };
        }));
    };

    // ─ Remove an exercise from a segment ─────────────────────────────
    const removeExerciseFromSeg = (segId, exId) => {
        setSegments(prev => prev.map(s => {
            if (s.id !== segId) return s;
            return { ...s, exercises: s.exercises.filter(e => e.id !== exId) };
        }));
    };

    // ─ Change type of one exercise in a segment ───────────────────────
    //
    //  Cambiar de tipo BORRA lo que hubiera escrito, y tiene que ser así: un
    //  quiz y un «completar» no comparten ni un campo. En el paso 2 eso daba
    //  igual porque todavía no hay nada; desde el paso 3 —donde ahora también
    //  se puede elegir— sí lo hay, así que:
    //
    //    · volver a pulsar el tipo que YA está puesto no hace nada. Antes
    //      vaciaba el ejercicio igual: un clic de más en el botón que ya estaba
    //      encendido y el trabajo de la IA se iba sin preguntar.
    //    · y cambiarlo de verdad con algo escrito se pregunta antes, arriba en
    //      la pantalla, no aquí.
    const setExerciseType = (segId, exId, type) => {
        setSegments(prev => prev.map(s => {
            if (s.id !== segId) return s;
            return {
                ...s,
                exercises: s.exercises.map(e => {
                    if (e.id !== exId || e.type === type) return e;
                    return { ...e, type, data: null, status: 'pending' };
                }),
            };
        }));
    };

    //  ── ELEGIR EL TIPO SIN VOLVER AL PASO 2 (06-10-2026) ──────────────
    //  Rob: «al dar clic en Añadir ejercicio solo pone un quiz... retroceder a
    //  Segmentos me puede romper lo que se está construyendo y validando».
    //  Tenía razón en la preocupación: volver al paso 2 no borra nada, pero
    //  obliga a dejar a medias la revisión y a encontrar otra vez dónde ibas.
    //
    //  Qué ejercicios YA GENERADOS tienen la fila de tipos abierta. Los que
    //  todavía no tienen nada escrito la enseñan siempre: ahí elegir no cuesta
    //  nada y es justo lo que hace falta nada más añadirlo.
    const [cambiandoTipo, setCambiandoTipo] = useState(() => new Set());

    const alternaCambioDeTipo = (segId, exId) => {
        const k = `${segId}:${exId}`;
        setCambiandoTipo(prev => {
            const s = new Set(prev);
            if (s.has(k)) s.delete(k); else s.add(k);
            return s;
        });
    };

    /**
     * Cambiar el tipo desde el paso 3, donde puede haber trabajo hecho.
     *
     * Se pregunta UNA vez y sólo cuando hay algo que perder. Preguntar siempre
     * —también con el ejercicio vacío— enseña a darle a «Aceptar» sin leer, y
     * entonces el aviso que importa tampoco se lee.
     */
    const cambiaTipoEnElPaso3 = (seg, ex, tipo, numeroDelSegmento, numeroDelEjercicio) => {
        if (ex.type === tipo) { setCambiandoTipo(prev => { const s = new Set(prev); s.delete(`${seg.id}:${ex.id}`); return s; }); return; }
        if (ex.data) {
            const nombre = EXERCISE_TYPES[tipo]?.label || tipo;
            const ok = window.confirm(
                `El ejercicio ${numeroDelEjercicio} del segmento #${numeroDelSegmento} ya está escrito.

`
                + `Cambiarlo a «${nombre}» lo borra: los dos tipos no comparten ningún campo, `
                + `así que hay que volver a generarlo.

¿Lo cambio?`
            );
            if (!ok) return;
        }
        setExerciseType(seg.id, ex.id, tipo);
        setCambiandoTipo(prev => { const s = new Set(prev); s.delete(`${seg.id}:${ex.id}`); return s; });
        //  Si estaba abierto para revisar, lo que había ya no existe: dejar la
        //  vista abierta enseñaría el hueco de un ejercicio que acaba de nacer.
        setEjerciciosAbiertos(prev => { const s = new Set(prev); s.delete(`${seg.id}:${ex.id}`); return s; });
    };

    // ─ Oír un fragmento, de su principio a su final ───────────────────
    //
    //  Empieza EXACTAMENTE en su inicio —no medio segundo antes, como hacía el
    //  «saltar a este momento» de antes— y para en su final. Así se oye lo que
    //  el fragmento dice que contiene, ni una palabra de más ni de menos: si
    //  entra tarde o se corta a media frase, se nota al oírlo.
    const oyeFragmento = (seg) => {
        setActiveSegIdx(seg.id);
        if (!playerRef.current || seg.startTime === null) return;

        //  Volver a pulsarlo lo para: es el mismo botón, y un botón que sólo
        //  sabe empezar obliga a buscar otro para parar.
        if (oyendoRef.current?.id === seg.id) {
            try { playerRef.current.pauseVideo(); } catch (_) { }
            oyendoRef.current = null;
            setOyendoFragmento(null);
            return;
        }

        const marca = { id: seg.id, fin: Number(seg.endTime) || 0 };
        oyendoRef.current = marca;
        setOyendoFragmento(marca);
        try {
            playerRef.current.seekTo(Math.max(0, seg.startTime), true);
            playerRef.current.playVideo();
        } catch (_) { }
        setCurrentTime(Math.max(0, seg.startTime));
    };

    // ══════════════════════════════════════════════════════════════════
    //  PASO 4 — AFINAR DÓNDE SALTA CADA EJERCICIO
    // ══════════════════════════════════════════════════════════════════

    const buscaEn = (segundos) => {
        if (!playerRef.current) return;
        const s = Math.max(0, segundos);
        try { playerRef.current.seekTo(s, true); } catch (_) { }
        setCurrentTime(s);
        //  Saltar a mano borra el rastro de la última parada: si no, volver
        //  atrás para repetir el mismo disparo no pararía la segunda vez.
        tiempoAnteriorRef.current = s;
        ultimaParadaRef.current = null;
        setDondeParo(null);
    };

    //  Rebobina a un punto y reproduce. Es la única forma de contestar la
    //  pregunta que trae a esta pantalla: ¿le falta un poco más de vídeo antes
    //  de preguntar?
    const oyeDesde = (segundos) => {
        buscaEn(segundos);
        if (!playerRef.current) return;
        try { playerRef.current.playVideo(); } catch (_) { }
    };

    const sigue = () => {
        if (!playerRef.current) return;
        setDondeParo(null);
        try { playerRef.current.playVideo(); } catch (_) { }
    };

    const alternaPlay = () => {
        if (!playerRef.current) return;
        try {
            const estado = playerRef.current.getPlayerState();
            if (estado === 1 || estado === 3) playerRef.current.pauseVideo();
            else playerRef.current.playVideo();
        } catch (_) { }
    };

    const mueveElDisparo = (segId, exId, segundos) => {
        setSegments(prev => mueveDisparo(prev, segId, exId, segundos, videoDuration));
        setDisparoElegido(`${segId}:${exId}`);
    };

    //  ── EL TECLADO DEL PASO 4 (06-10-2026) ────────────────────────────
    //  Rob: «las flechas izquierda y derecha para mover sutilmente... si decido
    //  continuar con enter es como play para continuar el vídeo, o el mismo
    //  space para pausar».
    //
    //  Afinar un disparo es mirar la pantalla y tocar de oído: ir al ratón a
    //  buscar un botón de 60 píxeles entre cada décima rompe justo eso. Con las
    //  flechas se ajusta sin dejar de mirar.
    //
    //  No se escucha nada si el cursor está escribiendo en algún sitio: una
    //  barra espaciadora que pausa el vídeo en vez de poner un espacio es de las
    //  cosas que más desconciertan.
    useEffect(() => {
        if (step !== 4) return;
        const enUnCampo = (el) => {
            const t = (el?.tagName || '').toLowerCase();
            return t === 'input' || t === 'textarea' || t === 'select' || el?.isContentEditable;
        };
        const alPulsar = (e) => {
            if (e.ctrlKey || e.metaKey || e.altKey) return;
            if (enUnCampo(e.target)) return;

            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                const punto = puntosDeDisparo(segments).find(p => p.clave === disparoElegido);
                if (!punto) return;
                e.preventDefault();
                //  Una décima a secas, y un segundo con Shift. Son los dos
                //  tamaños con los que se busca: el gordo para encontrar la
                //  frase y el fino para cuadrar el final de la palabra.
                const paso = (e.shiftKey ? 1 : 0.1) * (e.key === 'ArrowLeft' ? -1 : 1);
                mueveElDisparo(punto.segId, punto.exId, punto.disparo + paso);
                return;
            }
            if (e.key === ' ' || e.code === 'Space') { e.preventDefault(); alternaPlay(); return; }
            if (e.key === 'Enter') { e.preventDefault(); sigue(); }
        };
        window.addEventListener('keydown', alPulsar);
        return () => window.removeEventListener('keydown', alPulsar);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, segments, disparoElegido]);

    //  Al entrar al paso 4 se deja ESCRITO el momento de todos. Hasta aquí
    //  vivía sólo en la cabeza del servidor —cinco segundos después de empezar
    //  el fragmento— y cambiaba solo en cuanto alguien movía un segmento. A
    //  partir de ahora está en el dato y no se mueve si nadie lo mueve.
    useEffect(() => {
        if (step !== 4) return;
        setSegments(prev => {
            const { segments: fijados, tocados } = fijaLosQueFaltan(prev);
            if (tocados > 0) {
                showToast(`⏱️ ${tocados} ejercicios tomaron su momento por defecto — `
                    + `ya se pueden mover uno a uno`, 'info');
                return fijados;
            }
            return prev;
        });
        setDisparoElegido(prev => prev || (puntosDeDisparo(segments)[0]?.clave ?? null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    // ─ Precision editor controls ──────────────────────────────────────
    const togglePrecision = (segId) => {
        setOpenPrecision(prev => {
            const next = new Set(prev);
            if (next.has(segId)) next.delete(segId);
            else next.add(segId);
            return next;
        });
    };

    //  ── MOVER UN CORTE MUEVE AL VECINO ────────────────────────────────
    //  Rob: «si yo modifico un fragmento, por obvias razones le afecta el
    //  INICIO del otro segmento, NO al final, ¿si es así?».
    //
    //  Es así, y en los dos sentidos: mover el FIN de uno mueve el INICIO del
    //  siguiente, y mover el INICIO mueve el FIN del anterior. Un corte es UNO
    //  —donde acaba uno empieza el otro—, no dos números que puedan separarse.
    //
    //  Pero sólo la mitad estaba hecha. El panel decía «← #9 FIN se moverá con
    //  INICIO» y no lo hacía: propagaba el FIN hacia adelante y nada hacia
    //  atrás. Así que mover el INICIO de un fragmento dejaba un hueco mudo
    //  entre él y el anterior —o un solape, si se movía hacia atrás— y la
    //  pantalla aseguraba que no.
    //
    //  Un cartel que promete lo que el programa no hace es peor que no tener
    //  cartel: se confía en él.
    const updateSegmentTime = (segId, changes, propagate) => {
        setSegments(prev => {
            const idx = prev.findIndex(s => s.id === segId);
            if (idx === -1) return prev;
            const updated = [...prev];
            updated[idx] = { ...updated[idx], ...changes };
            if (propagate && changes.endTime !== undefined && idx + 1 < updated.length) {
                updated[idx + 1] = { ...updated[idx + 1], startTime: changes.endTime };
            }
            if (propagate && changes.startTime !== undefined && idx > 0) {
                updated[idx - 1] = { ...updated[idx - 1], endTime: changes.startTime };
            }
            return updated;
        });
    };

    // ─ Delete a single segment by id ──────────────────────────────────
    const deleteSegment = (segId) => {
        setSegments(prev => prev.filter(s => s.id !== segId));
    };

    // ─ Delete segments from index n onwards (1-based) ─────────────────
    const deleteFromSegment = (n) => {
        setSegments(prev => prev.slice(0, n - 1));
        setActiveSegIdx(prev => Math.min(prev, n - 2));
    };

    // ─ Generate one specific exercise (segId + exId) ──────────────────
    const generateOneExercise = useCallback(async (segId, exId) => {
        const seg = segments.find(s => s.id === segId);
        const ex = seg?.exercises.find(e => e.id === exId);
        if (!ex || ex.status === 'generating') return;

        setSegments(prev => prev.map(s => s.id !== segId ? s : {
            ...s,
            exercises: s.exercises.map(e =>
                e.id === exId ? { ...e, status: 'generating', data: null } : e
            ),
        }));

        try {
            const res = await fetch('/api/admin/edu-editor/generate-exercise', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
                body: JSON.stringify({
                    segment_text: seg.text,
                    exercise_type: ex.type,
                    language,
                    level,
                }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setSegments(prev => prev.map(s => s.id !== segId ? s : {
                ...s,
                exercises: s.exercises.map(e =>
                    e.id === exId ? { ...e, status: 'ready', data } : e
                ),
            }));
        } catch (err) {
            console.error('generate-exercise error:', err);
            setSegments(prev => prev.map(s => s.id !== segId ? s : {
                ...s,
                exercises: s.exercises.map(e =>
                    e.id === exId ? { ...e, status: 'error' } : e
                ),
            }));
            showToast('❌ Error generando ejercicio', 'error');
        }
    }, [segments, language, level, authToken]);

    // ─ Generate ALL pending exercises sequentially ────────────────────
    const generateAllExercises = async () => {
        const pending = [];
        for (const s of segments) {
            for (const e of s.exercises) {
                if (e.status !== 'ready') pending.push({ segId: s.id, exId: e.id });
            }
        }
        if (pending.length === 0) {
            showToast('✅ Todos los ejercicios ya están listos.', 'success');
            return;
        }
        setGeneratingAll(true);
        setGenProgress({ done: 0, total: pending.length });
        for (let i = 0; i < pending.length; i++) {
            await generateOneExercise(pending[i].segId, pending[i].exId);
            setGenProgress({ done: i + 1, total: pending.length });
            if (i < pending.length - 1) await new Promise(r => setTimeout(r, 400));
        }
        setGeneratingAll(false);
        showToast('🎉 ¡Todos los ejercicios generados!', 'success');
    };

    //  ── SÓLO EL TEXTO EN PANTALLA (14-10-2026) ───────────────────────
    //  Rob: «sigue saliendo sin subtítulos abajo, sólo sale «·»... me imagino que
    //  pasa porque no son ejercicios nuevos». Exacto: los vídeos publicados antes
    //  de hoy NO tienen el transcript guardado, y no hay migración posible porque
    //  ese texto nunca llegó a la base.
    //
    //  Pero sí hay cómo recuperarlo sin tocar nada más: se pega el transcript
    //  otra vez y se guarda SÓLO el texto. No pasa por «Crear Segmentos» —que
    //  reharía el troceado y se llevaría por delante los ejercicios—: se lee el
    //  transcript, se saca de él el texto de cada fragmento, y los segmentos y
    //  sus ejercicios se guardan tal y como están.
    const actualizaSoloElTexto = async () => {
        if (!editandoId || !rawTranscript.trim()) return;
        setLoading(true);
        try {
            const res = await fetch('/api/admin/edu-editor/parse-transcript', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
                body: JSON.stringify({ raw_text: rawTranscript }),
            });
            if (!res.ok) throw new Error('parse');
            const data = await res.json();
            const lineas = (data.lines || []).map(l => ({
                text: l.text, time: l.time, time_ms: l.time_ms,
                estimado: l.estimado, rotulo: l.rotulo === true,
            }));
            //  El mismo troceo que usa el editor, para que el texto caiga en los
            //  mismos segundos que si se hubiera rehecho todo. Lo que NO se hace
            //  es tocar `segments`.
            const trozos = trocea(lineas);
            const nuevas = trozos
                .filter(f => (f.text || '').trim() && f.endTime > f.startTime)
                .map(f => ({
                    start: Math.round(f.startTime * 10) / 10,
                    end: Math.round(f.endTime * 10) / 10,
                    text: f.text.trim(),
                }));

            if (!nuevas.length) {
                showToast('⚠️ No se sacó ni un renglón de ese transcript.', 'error');
                setLoading(false);
                return;
            }

            const { segments: intactos } = preparaParaGuardar(segments);
            const guarda = await fetch(`/api/admin/edu-editor/${editandoId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
                body: JSON.stringify({
                    edu_data: {
                        ...(rawTranscript ? { raw_transcript: rawTranscript } : {}),
                        segments: intactos,
                        captions: nuevas,
                    },
                }),
            });
            if (!guarda.ok) throw new Error('guardar');
            setCaptionsCargadas(nuevas);
            showToast(
                `💬 Texto en pantalla actualizado: ${nuevas.length} `
                + `${nuevas.length === 1 ? 'renglón' : 'renglones'} · los `
                + `${intactos.length} fragmentos y sus ejercicios, intactos.`,
                'success',
            );
        } catch {
            showToast('❌ No se pudo actualizar el texto. Revisa la conexión.', 'error');
        }
        setLoading(false);
    };

    // ─ Save to DB ─────────────────────────────────────────────────────
    const saveToDB = async () => {
        const { segments: paraGuardar, descartados, captions } = preparaParaGuardar(segments);

        //  ── EL TEXTO EN PANTALLA NO SE ENCOGE ─────────────────────────
        //  `captions` sale de los fragmentos que hay AHORA en el editor, y al
        //  reabrir un vídeo sólo se cargan los que llevan ejercicio: diez de
        //  noventa y nueve. Guardar eso encima del texto bueno sería borrarle
        //  al alumno el 90% del vídeo por haber entrado a arreglar una coma.
        //  Se queda el que más renglón tenga.
        const textoFinal = eligeElTexto(captions, captionsCargadas);
        if (paraGuardar.length === 0) {
            showToast('⚠️ Genera al menos un ejercicio antes de publicar.', 'warning');
            return;
        }

        //  ── LO QUE SE QUEDARÍA FUERA ──────────────────────────────────
        //  Un segmento sin ningún ejercicio escrito no se guarda. Al PUBLICAR
        //  uno nuevo eso da igual —no existía—, pero al CORREGIR uno guardado
        //  significa borrarlo de la base, porque el `edu_data` nuevo sustituye
        //  al viejo entero. Eso no puede pasar en silencio.
        if (editandoId && descartados.length > 0) {
            const lista = descartados
                .map(d => `   · #${d.numero}  ${d.texto}…`).join('\n');
            const ok = window.confirm(
                `${descartados.length === 1 ? 'Un segmento no tiene' : `${descartados.length} segmentos no tienen`}`
                + ` ningún ejercicio escrito:\n\n${lista}\n\n`
                + `Al guardar desaparecen del vídeo. Si querías conservarlos, cancela y `
                + `genera su ejercicio en el paso 3.\n\n¿Guardo de todas formas?`
            );
            if (!ok) return;
        }

        setLoading(true);
        try {
            const cuerpo = {
                youtube_id: extractId(videoId),
                title, channel, topic, language, level,
            };
            //  Dos rutas distintas a propósito. El POST crea un vídeo nuevo; si
            //  se usara también para corregir, cada arreglo de una coma dejaría
            //  un duplicado en el catálogo del alumno.
            const res = editandoId
                ? await fetch(`/api/admin/edu-editor/${editandoId}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken}`,
                    },
                    body: JSON.stringify({
                        ...cuerpo,
                        //  La transcripción importada se conserva: vive en el
                        //  mismo `edu_data` que se está sustituyendo, y sin
                        //  esto reabrir y guardar la borraría.
                        edu_data: {
                            ...(rawTranscript ? { raw_transcript: rawTranscript } : {}),
                            segments: paraGuardar,
                            //  El transcript entero, para el texto en pantalla
                            //  del alumno. `segments` sólo lleva los fragmentos
                            //  con ejercicio, que en un vídeo de 13 minutos son
                            //  diez rengloncitos sueltos.
                            ...(textoFinal.length ? { captions: textoFinal } : {}),
                        },
                    }),
                })
                : await fetch('/api/admin/edu-editor/', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken}`,
                    },
                    body: JSON.stringify({ ...cuerpo, segments: paraGuardar, captions: textoFinal }),
                });
            if (!res.ok) throw new Error();

            if (editandoId) {
                //  No se vacía el formulario: corregir casi nunca es una sola
                //  cosa, y dejarlo abierto es la diferencia entre arreglar tres
                //  faltas y tener que volver a entrar tres veces.
                showToast(`💾 Cambios guardados en «${title}»`, 'success');
            } else {
                showToast('🏆 ¡Video educativo publicado exitosamente!', 'success');
                setTimeout(() => {
                    setStep(1); setPasoMaximo(1); setSegments([]); setRawTranscript('');
                    setDesfaseGlobal(0); setConfirmaDesfase(null);
                    setVideoId(''); setTitle(''); setChannel('');
                }, 2500);
            }
        } catch {
            showToast('❌ Error al guardar. Verifica la conexión.', 'error');
        }
        setLoading(false);
    };

    // ══════════════════════════════════════════════════════════════════
    //  REVISAR LO QUE ESCRIBIÓ LA IA
    // ══════════════════════════════════════════════════════════════════

    const claveDe = (segId, exId) => `${segId}:${exId}`;

    const alternaVista = (segId, exId) => {
        const k = claveDe(segId, exId);
        setEjerciciosAbiertos(prev => {
            const s = new Set(prev);
            if (s.has(k)) s.delete(k); else s.add(k);
            return s;
        });
    };

    //  Abrir los veintitantos de golpe para bajar leyendo, y cerrarlos de golpe
    //  para volver a trabajar. Es un solo botón porque son la misma intención en
    //  los dos sentidos, y sabe en cuál está mirando lo que hay abierto: con dos
    //  botones separados siempre acabas pulsando el que no toca.
    const todosAbiertos = useMemo(() => {
        const claves = segments.flatMap(s => s.exercises.map(e => claveDe(s.id, e.id)));
        return claves.length > 0 && claves.every(k => ejerciciosAbiertos.has(k));
    }, [segments, ejerciciosAbiertos]);

    const alternaTodasLasVistas = () => {
        if (todosAbiertos) { setEjerciciosAbiertos(new Set()); return; }
        setEjerciciosAbiertos(new Set(
            segments.flatMap(s => s.exercises.map(e => claveDe(s.id, e.id)))
        ));
    };

    /**
     * Todo el vídeo —transcripción y ejercicios— al portapapeles, para pegarlo
     * en otra IA y que lo revise.
     *
     * El portapapeles del navegador sólo funciona en «contexto seguro» (https o
     * localhost) y además se puede denegar por permisos. Cuando no se puede, NO
     * se da por copiado: se abre una caja con el texto ya seleccionado para
     * hacerlo con Ctrl+C. Un «✅ copiado» falso es la peor versión de esto —
     * Rob se va a otra pestaña, pega, y no hay nada.
     */
    const copiaElInforme = async () => {
        const texto = armaInforme({
            meta: { title, channel, level, language, topic, videoId: extractId(videoId) },
            segments,
            etiquetas: ETIQUETAS_DE_TIPO,
        });
        try {
            if (!navigator.clipboard?.writeText) throw new Error('sin portapapeles');
            await navigator.clipboard.writeText(texto);
            showToast(`📋 Copiado: ${segments.length} segmentos y ${stats.total} ejercicios`, 'success');
        } catch {
            setInformeAMano(texto);
            showToast('El navegador no deja copiar solo — cópialo con Ctrl+C', 'info');
        }
    };

    /**
     * El encargo para otra IA: las instrucciones, el formato de los seis tipos,
     * y el vídeo partido en fragmentos con sus minutos exactos.
     *
     * Hermano de `copiaElInforme`, y hacen cosas distintas: aquél saca lo que YA
     * está escrito para que alguien lo revise; éste pide que lo escriban.
     */
    const copiaLaPlantilla = async () => {
        const texto = plantillaParaOtraIA({
            meta: { title, channel, level, language, topic },
            segments,
        });
        try {
            if (!navigator.clipboard?.writeText) throw new Error('sin portapapeles');
            await navigator.clipboard.writeText(texto);
            showToast(`🤖 Plantilla copiada: ${segments.length} fragmentos con sus tiempos`, 'success');
        } catch {
            setInformeAMano(texto);
            showToast('El navegador no deja copiar solo — cópialo con Ctrl+C', 'info');
        }
    };

    //  ── SE LEE SOLO (07-10-2026) ──────────────────────────────────────
    //  Había un botón «Leer» antes del de «Colocar», y «Colocar» salía apagado
    //  hasta pulsarlo. Rob pegó la respuesta de otra IA —perfecta, once
    //  ejercicios, cero rechazados— y lo que vio fue un botón muerto sin una
    //  palabra que dijera por qué.
    //
    //  Un botón apagado que no explica nada es un callejón sin salida: desde
    //  fuera no se distingue de «esto no funciona», y la conclusión razonable
    //  es que la culpa era de la otra IA.
    //
    //  Así que ya no hay que pedirlo: se lee al pegar. El medio segundo de
    //  espera es para no releer en cada tecla si alguien escribe a mano.
    useEffect(() => {
        if (!pegando) return;
        if (!textoPegado.trim()) { setLectura(null); return; }
        const t = setTimeout(() => setLectura(leeEjercicios(textoPegado, segments)), 350);
        return () => clearTimeout(t);
    }, [textoPegado, pegando, segments]);

    const colocaLoPegado = () => {
        if (!lectura?.entran?.length) return;
        //  Sustituir borra lo que hubiera. Se pregunta con el número delante:
        //  «se van a borrar los ejercicios» no dice cuántos, y cuántos es justo
        //  lo que hace dudar.
        if (modoPegado === 'sustituir') {
            const hechos = segments.reduce(
                (n, sg) => n + sg.exercises.filter(e => e.status === 'ready').length, 0);
            if (hechos > 0) {
                const ok = window.confirm(
                    `Sustituir borra los ${hechos} ejercicios que ya hay en este vídeo `
                    + `y deja sólo los ${lectura.entran.length} que acabas de pegar.\n\n¿Seguro?`);
                if (!ok) return;
            }
        }
        setSegments(prev => colocaEjercicios(prev, lectura.entran, modoPegado));
        showToast(
            `📥 ${lectura.entran.length} ejercicios colocados`
            + `${lectura.fuera.length ? ` · ${lectura.fuera.length} se quedaron fuera` : ''}`,
            'success',
        );
        setPegando(false);
        setTextoPegado('');
        setLectura(null);
    };

    //  Si hay que copiar a mano, el texto llega ya seleccionado: pedirle a
    //  alguien que seleccione 400 líneas con el ratón es no haber resuelto nada.
    useEffect(() => {
        if (informeAMano && cajaDelInforme.current) {
            cajaDelInforme.current.focus();
            cajaDelInforme.current.select();
        }
    }, [informeAMano]);

    // ─ Computed stats (over individual exercises) ─────────────────────
    const stats = useMemo(() => {
        let ready = 0, pending = 0, errors = 0;
        //  Las que llevan DENTRO su segundo de disparo: son las que se mueven
        //  con el desfase global. Una sin generar no tiene momento que mover.
        let conMomento = 0;
        const byType = {};
        for (const s of segments) {
            for (const e of s.exercises) {
                if (e.status === 'ready') ready++;
                else if (e.status === 'error') errors++;
                else pending++;
                if (e.data && typeof e.data === 'object') conMomento++;
                byType[e.type] = (byType[e.type] || 0) + 1;
            }
        }
        const total = ready + pending + errors;
        return {
            total, ready, pending, errors, byType, conMomento,
            pct: total ? Math.round(ready / total * 100) : 0,
            segCount: segments.length,
        };
    }, [segments]);

    // ══════════════════════════════════════════════════════════════════
    //  STEPS CONFIG
    // ══════════════════════════════════════════════════════════════════
    const STEPS = [
        { num: 1, label: 'Metadata' },
        { num: 2, label: 'Segmentos' },
        { num: 3, label: 'Generar IA' },
        { num: 4, label: 'Tiempos' },
        { num: 5, label: 'Preview' },
    ];

    // ══════════════════════════════════════════════════════════════════
    //  RENDER
    // ══════════════════════════════════════════════════════════════════
    return (
        <div className="ee-root">

            {/* ── HEADER ── */}
            <header className="ee-header">
                <div className="ee-brand">
                    <div className="ee-brand-icon">🎓</div>
                    <div>
                        <div className="ee-brand-title">Edu Video Editor</div>
                        <div className="ee-brand-sub">NSL Exercise Engine v1.0</div>
                    </div>
                </div>

                <nav className="ee-stepper">
                    {STEPS.map((s, i) => (
                        <React.Fragment key={s.num}>
                            <button
                                className={`ee-step-pill ${step === s.num ? 'active' : step > s.num ? 'done' : ''}`}
                                onClick={() => s.num <= pasoMaximo && vaAlPaso(s.num)}
                                disabled={s.num > pasoMaximo}
                            >
                                <span className="ee-step-n">{step > s.num ? '✓' : s.num}</span>
                                <span>{s.label}</span>
                            </button>
                            {i < STEPS.length - 1 && (
                                <div className={`ee-step-connector ${step > s.num ? 'done' : ''}`} />
                            )}
                        </React.Fragment>
                    ))}
                </nav>

                {step >= 3 && (
                    <div className="ee-header-stats">
                        <div className="ee-stat-badge" style={{ '--c': '#34d399' }}>
                            ✅ {stats.ready}
                        </div>
                        <div className="ee-stat-badge" style={{ '--c': '#fb923c' }}>
                            ⏳ {stats.pending + (generatingAll ? 0 : stats.errors)}
                        </div>
                        <div className="ee-stat-badge" style={{ '--c': '#a78bfa' }}>
                            📊 {stats.pct}%
                        </div>
                    </div>
                )}
            </header>

            {/* ── TOAST ── */}
            {toast.text && (
                <div className={`ee-toast ee-toast--${toast.type}`}>
                    {toast.text}
                </div>
            )}

            {/* ── PEGAR TODOS LOS EJERCICIOS DE UNA VEZ ────────────────────────
                Rob: «un súper poder más a los vídeos: pegar aquí todos los
                ejercicios... pero de un solo golpe». */}
            {pegando && (
                <div className="ee-pegar" role="dialog" aria-label="Pegar ejercicios">
                    <div className="ee-pegar-caja">
                        <div className="ee-pegar-head">
                            <strong>📥 Pegar los ejercicios de otra IA</strong>
                            <span>
                                Pega su respuesta entera. Si viene con «aquí tienes» o entre
                                ```, da igual: se busca el JSON dentro.
                            </span>
                            <button className="ee-btn-ghost"
                                    onClick={() => { setPegando(false); setLectura(null); }}>
                                Cerrar
                            </button>
                        </div>

                        <textarea
                            className="ee-pegar-texto"
                            value={textoPegado}
                            autoFocus
                            placeholder={'{\n  "ejercicios": [\n    { "fragmento": 1, "type": "quiz", … }\n  ]\n}'}
                            onChange={(e) => { setTextoPegado(e.target.value); setLectura(null); }}
                        />

                        {/* Lo que se leyó, antes de tocar nada. */}
                        {lectura && (
                            <div className="ee-pegar-lectura">
                                {lectura.cortada && (
                                    <div className="ee-pegar-mal">
                                        ✂️ La respuesta venía <strong>cortada</strong> — el chat de
                                        la otra IA la truncó. Se ha rescatado lo que llegó entero.
                                        Si falta mucho, pídesela por partes: «los fragmentos 1 al
                                        20», y luego el resto.
                                    </div>
                                )}
                                {lectura.entran.length > 0 && (
                                    <div className="ee-pegar-ok">
                                        ✅ {lectura.entran.length} ejercicios listos para colocar
                                        <span className="ee-pegar-detalle">
                                            {[...new Set(lectura.entran.map(e => `#${e.numero}`))]
                                                .slice(0, 14).join(' ')}
                                            {new Set(lectura.entran.map(e => e.numero)).size > 14 ? ' …' : ''}
                                        </span>
                                    </div>
                                )}
                                {/* ── TODAS LA MISMA LETRA ────────────────────
                                    Rob, 14-10-2026: «todas las respuestas del quiz
                                    son A». Se le pide a la otra IA que las reparta,
                                    pero una instrucción no es una garantía: hay
                                    que MIRARLO. Y no se ve revisando los ejercicios
                                    de uno en uno —cada uno está bien—, sólo
                                    contándolos todos juntos. */}
                                {lectura.sesgo && (
                                    <div className="ee-pegar-sesgo" data-prueba="edu-pegar-sesgo">
                                        🎯 <strong>{lectura.sesgo.cuantos} de {lectura.sesgo.total}</strong>
                                        {' '}respuestas correctas son la <strong>{lectura.sesgo.letra}</strong>
                                        {' '}({lectura.sesgo.reparto.join(' · ')}).
                                        <span className="ee-pegar-detalle">
                                            El alumno lo nota a la tercera pregunta y deja de
                                            escuchar el vídeo: pulsa esa y acierta. Pide a la otra
                                            IA que reparta la correcta entre las cuatro posiciones.
                                        </span>
                                    </div>
                                )}
                                {/* Lo que NO entra se dice uno a uno, con su motivo.
                                    Colarlo a medias es lo mismo que no revisarlo:
                                    sale verde aquí y falla en el juego del alumno. */}
                                {lectura.fuera.map((f, i) => (
                                    <div key={i} className="ee-pegar-mal">
                                        ⚠️ <strong>{f.donde}</strong>: {f.motivo}
                                    </div>
                                ))}
                                {!lectura.entran.length && lectura.motivo && (
                                    <div className="ee-pegar-mal">⚠️ {lectura.motivo}</div>
                                )}
                            </div>
                        )}

                        <div className="ee-pegar-pie">
                            <label className="ee-pegar-modo">
                                <input
                                    type="radio"
                                    checked={modoPegado === 'añadir'}
                                    onChange={() => setModoPegado('añadir')}
                                />
                                Añadir a lo que ya hay
                            </label>
                            <label className="ee-pegar-modo">
                                <input
                                    type="radio"
                                    checked={modoPegado === 'sustituir'}
                                    onChange={() => setModoPegado('sustituir')}
                                />
                                Sustituirlo todo
                            </label>
                            {/* Y si no hay nada que colocar, se dice ahí mismo.
                                Un botón apagado y mudo es un callejón sin
                                salida. */}
                            {!lectura?.entran?.length && (
                                <span className="ee-pegar-espera">
                                    {textoPegado.trim()
                                        ? (lectura ? 'Nada que colocar todavía' : 'Leyendo…')
                                        : 'Pega aquí la respuesta de la otra IA'}
                                </span>
                            )}
                            <button className="ee-btn-primary" onClick={colocaLoPegado}
                                    disabled={!lectura?.entran?.length}>
                                Colocar {lectura?.entran?.length || ''}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── CUANDO EL NAVEGADOR NO DEJA COPIAR SOLO ──────────────────────
                El portapapeles exige «contexto seguro» (https o localhost) y
                encima se puede denegar por permisos. En vez de mentir con un
                «copiado» que no pasó, el texto aparece aquí ya seleccionado. */}
            {informeAMano && (
                <div className="ee-copiar-a-mano" role="dialog" aria-label="Copiar el informe a mano">
                    <div className="ee-copiar-a-mano-caja">
                        <div className="ee-copiar-a-mano-head">
                            <strong>Cópialo con Ctrl+C</strong>
                            <span>Ya está todo seleccionado. Este navegador no deja copiar por su cuenta.</span>
                            <button className="ee-btn-ghost" onClick={() => setInformeAMano('')}>Cerrar</button>
                        </div>
                        <textarea
                            ref={cajaDelInforme}
                            className="ee-copiar-a-mano-texto"
                            readOnly
                            value={informeAMano}
                            onFocus={(e) => e.target.select()}
                        />
                    </div>
                </div>
            )}

            <div className="ee-body">

                {/* ════════════ STEP 1 — METADATA ════════════ */}
                {step === 1 && (
                    <div className="ee-step1">
                        <div className="ee-meta-grid">
                            <div className="ee-field span2">
                                <label>YouTube URL o Video ID</label>
                                <input
                                    value={videoId}
                                    onChange={e => setVideoId(e.target.value)}
                                    placeholder="https://youtube.com/watch?v=... o solo el ID"
                                />
                            </div>
                            <div className="ee-field">
                                <label>Título del video</label>
                                <input
                                    value={title}
                                    onChange={e => setTitle(e.target.value)}
                                    placeholder="The Future of AI — Interview"
                                />
                            </div>
                            <div className="ee-field">
                                <label>Canal / Fuente</label>
                                <input
                                    value={channel}
                                    onChange={e => setChannel(e.target.value)}
                                    placeholder="TED, CNN, BBC Learning..."
                                />
                            </div>
                            <div className="ee-field">
                                <label>Tema / Categoría</label>
                                <select value={topic} onChange={e => setTopic(e.target.value)}>
                                    {TOPICS.map(t => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="ee-field">
                                <label>Nivel del alumno</label>
                                <select value={level} onChange={e => setLevel(e.target.value)}>
                                    {LEVELS.map(l => (
                                        <option key={l.value} value={l.value}>{l.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Transcript Input */}
                        <div className="ee-transcript-card">
                            <div className="ee-transcript-header">
                                <div>
                                    <div className="ee-field-label">Transcript del Video</div>
                                    <div className="ee-field-hint">
                                        Pega el transcript de YouTube. Soporta timestamps <code>0:14</code>,
                                        formato de accesibilidad <code>"1 minute, 6 seconds"</code> y LRC <code>[00:14.50]</code>
                                    </div>
                                </div>
                            </div>
                            {/* La misma caja que el Sync Editor. Aquí también
                                hacía falta: con el transcript de una entrevista
                                de veinte minutos, encontrar dónde empieza cada
                                intervención en un muro de texto blanco es el
                                trabajo lento de verdad. */}
                            <CajaDeTranscripcion
                                valor={rawTranscript}
                                alCambiar={setRawTranscript}
                                prefijo="edu"
                                placeholder={`Pega aquí el transcript con sus tiempos.\n\nDel panel de YouTube:\n0:05\n5 seconds\nArtificial intelligence is transforming the way we work.\n\nO mejor: el subtítulo .srt o .vtt del vídeo, que trae milisegundos.\n00:00:05,120 --> 00:00:11,400\nArtificial intelligence is transforming the way we work.`}
                            />
                            <div className="ee-transcript-footer">
                                {/* Aquí estuvo media tarde una casilla para elegir
                                    cuántas líneas entraban en cada fragmento. Se fue:
                                    el transcript ya viene cortado y lo que se pega es
                                    lo que sale. */}
                                <span className="ee-troceo-nota">
                                    Cada marca de tiempo del transcript será un fragmento.
                                </span>
                                <button
                                    className="ee-btn-primary"
                                    disabled={!videoId || !rawTranscript || !title}
                                    onClick={parseAndSegment}
                                >
                                    Crear Segmentos →
                                </button>

                                {/* ── SÓLO EL TEXTO, SIN TOCAR LOS EJERCICIOS ──
                                    Para los vídeos publicados antes de que se
                                    guardara el transcript: se pega otra vez y se
                                    actualiza sólo lo que el alumno lee en
                                    pantalla. «Crear Segmentos» no vale aquí,
                                    porque rehace el troceado y se lleva por
                                    delante los ejercicios ya escritos. */}
                                {editandoId && (
                                    <button
                                        className="ee-btn-texto-solo"
                                        data-prueba="edu-solo-texto"
                                        disabled={!rawTranscript || loading}
                                        onClick={actualizaSoloElTexto}
                                        title={'Guarda el texto que el alumno lee en pantalla '
                                            + '(CC) sin tocar los fragmentos ni los ejercicios'}
                                    >
                                        💬 Sólo el texto en pantalla
                                        <span className="ee-btn-texto-solo-nota">
                                            no toca los ejercicios
                                        </span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* ════════════ STEPS 2-4 — DUAL PANE ════════════ */}
                {step >= 2 && (
                    <div className="ee-editor">

                        {/* ─── LEFT PANE ─── */}
                        <div className="ee-left">

                            {/* Player */}
                            <div id="edu-yt-player" ref={ytContainer} className="ee-player" />

                            {/* Current time */}
                            <div className="ee-transport">
                                <span className="ee-t-label">▶ Posición</span>
                                <span className="ee-t-time">{formatTime(currentTime)}</span>
                                <span className="ee-t-seg">Seg. {activeSegIdx + 1}/{segments.length}</span>
                            </div>

                            {/* Step 2 panel: segment controls */}
                            {step === 2 && (
                                <div className="ee-panel">
                                    <div className="ee-panel-title">⚙️ Configuración de Segmentos</div>
                                    <p className="ee-panel-hint">
                                        {lineasLeidas.length > 0 ? (
                                            <>
                                                <strong>{lineasLeidas.length}</strong> líneas del
                                                transcript, <strong>{segments.length}</strong> fragmentos:
                                                uno por marca de tiempo. Asigna el tipo de ejercicio
                                                a cada uno y avanza a generar con IA.
                                            </>
                                        ) : (
                                            <>El transcript fue dividido en <strong>{segments.length}</strong> segmentos.
                                            Asigna el tipo de ejercicio a cada uno y luego avanza a generar con IA.</>
                                        )}
                                    </p>

                                    <div className="ee-type-legend">
                                        {Object.entries(EXERCISE_TYPES).map(([key, cfg]) => (
                                            <div key={key} className="ee-legend-item" style={{ '--ec': cfg.color }}>
                                                <div className="ee-legend-dot" />
                                                <span>{cfg.emoji} {cfg.label}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="ee-stat-row">
                                        {Object.entries(stats.byType).map(([type, count]) => (
                                            <div key={type} className="ee-type-count" style={{ '--ec': EXERCISE_TYPES[type]?.color }}>
                                                {EXERCISE_TYPES[type]?.emoji} {count}
                                            </div>
                                        ))}
                                    </div>

                                    {/* ── MOVER TODOS LOS FRAGMENTOS A LA VEZ ──────────
                                        Rob, 14-10-2026: «a veces todos tienen un desfase
                                        pequeño de .5 s y tengo que abrir todos para ajustar
                                        esos segundos y me quita mucho tiempo... siempre me
                                        pasa al terminar las oraciones».

                                        Es el mismo mando que el de un fragmento suelto, a
                                        propósito: los mismos pasos y el mismo orden, para
                                        que no haya que aprender dos. Lo que cambia es el
                                        aviso de arriba, que dice a cuántos les va a dar. */}
                                    <div className="ee-desfase" data-prueba="edu-desfase">
                                        <div className="ee-field-label" style={{ marginBottom: 4 }}>
                                            🎚️ Mover TODOS los fragmentos
                                        </div>
                                        <p className="ee-desfase-hint">
                                            Un clic mueve los <strong>{segments.length}</strong> fragmentos
                                            {stats.conMomento > 0 && (
                                                <> y el momento de sus <strong>{stats.conMomento}</strong> preguntas</>
                                            )} a la vez. Para cuando el transcript viene corrido
                                            por igual en todo el vídeo.
                                        </p>
                                        <div className="ee-desfase-btns">
                                            <button className="ee-ndg n" data-prueba="edu-desfase-m1"
                                                    onClick={() => mueveTodosLosFragmentos(-1)}>−1s</button>
                                            <button className="ee-ndg n" data-prueba="edu-desfase-m05"
                                                    onClick={() => mueveTodosLosFragmentos(-0.5)}>−.5</button>
                                            <button className="ee-ndg n" data-prueba="edu-desfase-m01"
                                                    onClick={() => mueveTodosLosFragmentos(-0.1)}>−.1</button>
                                            <div className="ee-ndg-sep" />
                                            <button className="ee-ndg p" data-prueba="edu-desfase-p01"
                                                    onClick={() => mueveTodosLosFragmentos(0.1)}>+.1</button>
                                            <button className="ee-ndg p" data-prueba="edu-desfase-p05"
                                                    onClick={() => mueveTodosLosFragmentos(0.5)}>+.5</button>
                                            <button className="ee-ndg p" data-prueba="edu-desfase-p1"
                                                    onClick={() => mueveTodosLosFragmentos(1)}>+1s</button>
                                        </div>
                                        {/* ↩ El acumulado LLEVA EL NÚMERO dentro, como en el
                                            editor de canciones: un ↩ pelado no dice adónde
                                            vuelve, y para cuando hace falta ya no se recuerda
                                            de dónde se partió — ahí está el error de dedo. */}
                                        <div className="ee-desfase-pie">
                                            <span
                                                className={`ee-desfase-chip${desfaseGlobal ? ' vivo' : ''}`}
                                                data-prueba="edu-desfase-acumulado"
                                            >
                                                {desfaseGlobal
                                                    ? `${desfaseGlobal > 0 ? '+' : ''}${desfaseGlobal} s movido`
                                                    : 'sin desfase'}
                                            </span>
                                            <button
                                                className="ee-desfase-undo"
                                                data-prueba="edu-desfase-deshacer"
                                                disabled={!desfaseGlobal}
                                                onClick={quitaElDesfaseGlobal}
                                                title={desfaseGlobal
                                                    ? `Devuelve los ${segments.length} fragmentos a donde `
                                                      + 'estaban al trocear'
                                                    : 'No se ha movido nada todavía'}
                                            >
                                                ↩ Quitar el desfase
                                            </button>
                                        </div>

                                        {/* ── CUANDO NO CABE: SE PREGUNTA ──────────────
                                            Rob, 14-10-2026: «le di al +.5 y me marcó
                                            errores, lo cual es correcto... pero por eso
                                            ese botón increíble dejó de funcionar. Lo
                                            ideal es que el sistema sea más inteligente y
                                            me avise, yo confirmo si hacerlo de todos
                                            modos».

                                            Se dice lo que pasa y decide él. Y lo primero
                                            que se dice es si el vídeo YA venía así, que
                                            es lo que de verdad explica lo que está
                                            viendo. */}
                                        {confirmaDesfase && (
                                            <div className="ee-desfase-confirma"
                                                 data-prueba="edu-desfase-confirma">
                                                <div className="ee-desfase-confirma-tit">
                                                    ⚠️ No cabe entero hacia adelante
                                                </div>
                                                {confirmaDesfase.yaFuera.fragmentos > 0 ? (
                                                    <p className="ee-desfase-confirma-txt">
                                                        Este transcript <strong>ya es más largo que el
                                                        vídeo</strong>: {confirmaDesfase.yaFuera.fragmentos} de
                                                        los {confirmaDesfase.fragmentos} fragmentos empiezan
                                                        después del final ({relojFino(videoDuration)}) desde
                                                        antes de tocar este botón. Moverlos
                                                        {' '}+{confirmaDesfase.pedido} s no lo empeora, pero
                                                        tampoco lo arregla: eso se mira en el transcript o en
                                                        el enlace del vídeo.
                                                    </p>
                                                ) : (
                                                    <p className="ee-desfase-confirma-txt">
                                                        Moverlos +{confirmaDesfase.pedido} s dejaría
                                                        {' '}<strong>{confirmaDesfase.quedarianFuera.fragmentos}</strong>
                                                        {' '}fragmento{confirmaDesfase.quedarianFuera.fragmentos === 1 ? '' : 's'}
                                                        {confirmaDesfase.quedarianFuera.disparos > 0 && (
                                                            <> y {confirmaDesfase.quedarianFuera.disparos} pregunta
                                                            {confirmaDesfase.quedarianFuera.disparos === 1 ? '' : 's'}</>
                                                        )} <strong>empezando después del
                                                        final</strong> del vídeo ({relojFino(videoDuration)}).
                                                        Un fragmento que empieza ahí no suena nunca, y una
                                                        pregunta que salta ahí no salta nunca.
                                                        {' '}El final del último fragmento sí puede pasarse:
                                                        eso no rompe nada.
                                                    </p>
                                                )}
                                                <p className="ee-desfase-confirma-txt tenue">
                                                    Se mueve todo de una pieza, así que el ↩ lo devuelve
                                                    entero. Nada se aplasta ni se pierde.
                                                </p>
                                                <div className="ee-desfase-confirma-btns">
                                                    <button
                                                        className="ee-desfase-si"
                                                        data-prueba="edu-desfase-igual"
                                                        onClick={() => aplicaElDesfase(
                                                            confirmaDesfase.pedido,
                                                            'movidos aunque algunos queden pasados del final',
                                                        )}
                                                    >
                                                        Moverlos igual +{confirmaDesfase.pedido} s
                                                    </button>
                                                    <button
                                                        className="ee-desfase-no"
                                                        data-prueba="edu-desfase-dejarlo"
                                                        onClick={() => setConfirmaDesfase(null)}
                                                    >
                                                        Dejarlo
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* ── Borrado rápido de segmentos ── */}
                                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                                        <div className="ee-field-label" style={{ marginBottom: 6 }}>
                                            ✂️ Borrar desde segmento #
                                        </div>
                                        <div style={{ display: 'flex', gap: 6 }}>
                                            <input
                                                type="number"
                                                min={1}
                                                max={segments.length}
                                                value={deleteRange}
                                                onChange={e => setDeleteRange(e.target.value)}
                                                placeholder={`1–${segments.length}`}
                                                style={{
                                                    flex: 1,
                                                    padding: '7px 10px',
                                                    background: 'rgba(255,255,255,.04)',
                                                    border: '1px solid var(--border2)',
                                                    borderRadius: 'var(--r-sm)',
                                                    color: 'var(--txt)',
                                                    fontFamily: 'var(--mono)',
                                                    fontSize: 13,
                                                    outline: 'none',
                                                }}
                                            />
                                            <button
                                                onClick={() => {
                                                    const n = parseInt(deleteRange, 10);
                                                    if (!isNaN(n) && n >= 1 && n <= segments.length) {
                                                        deleteFromSegment(n);
                                                    }
                                                }}
                                                disabled={!deleteRange || parseInt(deleteRange) < 1 || parseInt(deleteRange) > segments.length}
                                                style={{
                                                    padding: '7px 12px',
                                                    background: 'rgba(248,113,113,.15)',
                                                    border: '1px solid var(--red)',
                                                    borderRadius: 'var(--r-sm)',
                                                    color: 'var(--red)',
                                                    fontFamily: 'var(--sans)',
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    cursor: 'pointer',
                                                    opacity: (!deleteRange || parseInt(deleteRange) < 1) ? 0.4 : 1,
                                                    transition: 'all .15s',
                                                }}
                                            >
                                                🗑️ Borrar
                                            </button>
                                        </div>
                                        {deleteRange && parseInt(deleteRange) >= 1 && parseInt(deleteRange) <= segments.length && (
                                            <div style={{ fontSize: 10, color: 'var(--txt-dim)', marginTop: 4, fontFamily: 'var(--mono)' }}>
                                                Se borrarán los segmentos #{deleteRange} al #{segments.length}
                                                ({segments.length - parseInt(deleteRange) + 1} segmentos)
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Step 3 panel: generation controls */}
                            {step === 3 && (
                                <div className="ee-panel">
                                    <div className="ee-panel-title">🤖 Motor de Generación IA</div>
                                    <p className="ee-panel-hint">
                                        La IA analizará cada segmento y generará el ejercicio
                                        según el tipo asignado.
                                    </p>

                                    {generatingAll && (
                                        <div className="ee-gen-progress">
                                            <div className="ee-gen-bar-track">
                                                <div
                                                    className="ee-gen-bar-fill"
                                                    style={{ width: `${(genProgress.done / genProgress.total) * 100}%` }}
                                                />
                                            </div>
                                            <span className="ee-gen-label">
                                                {genProgress.done} / {genProgress.total}
                                            </span>
                                        </div>
                                    )}

                                    <div className="ee-stats-grid">
                                        <div className="ee-sg-item green">
                                            <span>✅ Listos</span>
                                            <b>{stats.ready}</b>
                                        </div>
                                        <div className="ee-sg-item orange">
                                            <span>⏳ Pendientes</span>
                                            <b>{stats.pending}</b>
                                        </div>
                                        <div className="ee-sg-item red">
                                            <span>❌ Errores</span>
                                            <b>{stats.errors}</b>
                                        </div>
                                        <div className="ee-sg-item purple">
                                            <span>📊 Progreso</span>
                                            <b>{stats.pct}%</b>
                                        </div>
                                    </div>

                                    <button
                                        className="ee-btn-generate"
                                        onClick={generateAllExercises}
                                        disabled={generatingAll || stats.pending === 0}
                                    >
                                        {generatingAll
                                            ? `⏳ Generando... (${genProgress.done}/${genProgress.total})`
                                            : `🤖 Generar ${stats.pending > 0 ? stats.pending : 'todos los'} ejercicios`
                                        }
                                    </button>
                                </div>
                            )}

                            {/* Step 4 panel — tiempos */}
                            {step === 4 && (
                                <div className="ee-panel">
                                    <div className="ee-panel-title">⏱️ Dónde salta cada ejercicio</div>
                                    <p className="ee-panel-hint">
                                        Dale al play, escucha, y mueve la burbuja hasta el punto
                                        exacto en el que la pregunta tiene sentido. El alumno verá
                                        el vídeo pararse justo ahí.
                                    </p>
                                    <div className="ee-preview-meta">
                                        <div>📹 {title}</div>
                                        <div>⏱️ {puntosDeDisparo(segments).length} disparos</div>
                                    </div>
                                </div>
                            )}

                            {/* Step 5 panel */}
                            {step === 5 && (
                                <div className="ee-panel">
                                    <div className="ee-panel-title">🎮 Preview del Curso</div>
                                    <p className="ee-panel-hint">
                                        Revisa cada ejercicio. Dale Play y verifica que los tiempos
                                        coincidan con el contenido.
                                    </p>
                                    <div className="ee-preview-meta">
                                        <div>📹 {title}</div>
                                        <div>🎓 {level} · {language}</div>
                                        <div>📚 {stats.ready}/{stats.total} ejercicios</div>
                                    </div>
                                </div>
                            )}

                            {/* Navigation */}
                            <div className="ee-nav-panel">
                                {step === 2 && (
                                    <button className="ee-btn-primary" onClick={() => vaAlPaso(3)}>
                                        Ir a Generar IA →
                                    </button>
                                )}
                                {step === 3 && (
                                    <button
                                        className="ee-btn-primary"
                                        disabled={stats.ready === 0}
                                        onClick={() => vaAlPaso(4)}
                                    >
                                        Ajustar tiempos →
                                    </button>
                                )}
                                {step === 4 && (
                                    <button className="ee-btn-primary" onClick={() => vaAlPaso(5)}>
                                        Ver Preview →
                                    </button>
                                )}
                                {step === 5 && (
                                    <button
                                        className="ee-btn-success"
                                        onClick={saveToDB}
                                        disabled={loading || stats.ready === 0}
                                    >
                                        {loading
                                            ? (editandoId ? '⏳ Guardando...' : '⏳ Publicando...')
                                            : (editandoId ? '💾 Guardar cambios' : '🚀 Publicar Video')}
                                    </button>
                                )}
                                <button
                                    className="ee-btn-ghost"
                                    onClick={() => setStep(s => Math.max(1, s - 1))}
                                >
                                    ← Paso anterior
                                </button>
                            </div>
                        </div>

                        {/* ─── RIGHT PANE — Segments List ─── */}
                        <div className="ee-right">
                            <div className="ee-right-header">
                                <span className="ee-right-title">
                                    {step === 2 && '⚙️ Asignar tipos de ejercicio'}
                                    {step === 3 && '🤖 Generar ejercicios'}
                                    {step === 4 && '⏱️ Afinar los disparos'}
                                    {step === 5 && '🎮 Preview interactivo'}
                                </span>
                                <div className="ee-right-acciones">
                                    {/* ── REVISAR (05-10-2026) ──────────────────────────
                                        Sólo en los pasos donde ya hay ejercicios escritos.
                                        En el 2 todavía no existen, y en el 1 ni segmentos. */}
                                    {step >= 3 && segments.length > 0 && (
                                        <>
                                            {step === 3 && (
                                                <button
                                                    className="ee-right-accion"
                                                    onClick={alternaTodasLasVistas}
                                                    title="Abrir o cerrar todos los ejercicios a la vez"
                                                >
                                                    {todosAbiertos ? '👁 Cerrar todos' : '👁 Ver todos'}
                                                </button>
                                            )}
                                            <button
                                                className="ee-right-accion destacada"
                                                onClick={copiaElInforme}
                                                title="Todo el vídeo en texto —transcripción y ejercicios— para pegarlo en otra IA"
                                            >
                                                📋 Copiar todo
                                            </button>
                                            {step === 3 && (
                                                <>
                                                    <button
                                                        className="ee-right-accion"
                                                        onClick={copiaLaPlantilla}
                                                        title="El encargo para otra IA: el formato de los seis tipos y el vídeo partido en fragmentos con sus minutos"
                                                    >
                                                        🤖 Plantilla para otra IA
                                                    </button>
                                                    <button
                                                        className="ee-right-accion destacada"
                                                        onClick={() => setPegando(true)}
                                                        title="Pegar de una vez todos los ejercicios que escribió la otra IA"
                                                    >
                                                        📥 Pegar ejercicios
                                                    </button>
                                                </>
                                            )}
                                        </>
                                    )}
                                    <span className="ee-right-count">{segments.length} segmentos</span>
                                </div>
                            </div>

                            {step === 4 && (
                                <AjusteDeTiempos
                                    segments={segments}
                                    duracion={videoDuration}
                                    tiempo={currentTime}
                                    reproduciendo={reproduciendo}
                                    seleccion={disparoElegido}
                                    onSelecciona={setDisparoElegido}
                                    onMueve={mueveElDisparo}
                                    onBusca={buscaEn}
                                    onOye={oyeDesde}
                                    onPlayPause={alternaPlay}
                                    onSigue={sigue}
                                    pararEnDisparos={pararEnDisparos}
                                    onCambiaParada={setPararEnDisparos}
                                    dondeParo={dondeParo}
                                />
                            )}

                            <div className="ee-segments-scroll" ref={segListRef}
                                 style={step === 4 ? { display: 'none' } : undefined}>
                                {segments.map((seg, idx) => {
                                    const isActive = idx === activeSegIdx;
                                    const segReady = seg.exercises.some(e => e.status === 'ready');
                                    const segStatus = segReady ? 'ready' : seg.exercises.some(e => e.status === 'error') ? 'error' : 'pending';
                                    return (
                                        <div
                                            key={seg.id}
                                            className={`ee-seg-card ${isActive ? 'active' : ''} status-${segStatus}`}
                                        >
                                            {/* Segment Header */}
                                            <div className="ee-seg-head">
                                                <div className="ee-seg-info">
                                                    <span className="ee-seg-num">#{idx + 1}</span>
                                                    <button
                                                        className={`ee-seg-time ${oyendoFragmento?.id === seg.id ? 'sonando' : ''}`}
                                                        onClick={() => oyeFragmento(seg)}
                                                        title="Oír sólo este fragmento, de su principio a su final"
                                                    >
                                                        {oyendoFragmento?.id === seg.id ? '⏸' : '▶'}
                                                        {' '}{formatTimeFino(seg.startTime)} → {formatTimeFino(seg.endTime)}
                                                    </button>
                                                    {step === 2 && (
                                                        <button
                                                            className={`ee-seg-precision-toggle ${openPrecision.has(seg.id) ? 'active' : ''}`}
                                                            onClick={() => togglePrecision(seg.id)}
                                                            title="Ajuste de precisión"
                                                        >
                                                            ⚡ {openPrecision.has(seg.id) ? 'CERRAR' : 'EDITAR'}
                                                        </button>
                                                    )}
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    {seg.exercises.some(e => e.status === 'generating') && <span className="ee-spin" title="Generando...">⏳</span>}
                                                    {segReady && <span title="Listo">✅</span>}
                                                    {!segReady && seg.exercises.some(e => e.status === 'error') && <span title="Error">❌</span>}

                                                    <button
                                                        className="ee-delete-seg-btn"
                                                        onClick={() => {
                                                            if (segReady) {
                                                                if (!window.confirm(`¿Borrar segmento #${idx + 1}? Ya tiene ejercicios generados.`)) return;
                                                            }
                                                            deleteSegment(seg.id);
                                                        }}
                                                        title={`Borrar segmento #${idx + 1}`}
                                                    >
                                                        🗑️
                                                    </button>
                                                </div>
                                            </div>

                                            {/* ── EL RÓTULO DEL CAPÍTULO, SU PROPIA TARJETA ────
                                                Rob: «sería mejor o eliminarlo, o poner una card
                                                de "aquí continúa el Chapter 2"... sin afectar los
                                                tiempos».

                                                No lo dice nadie en el vídeo: es un cartel del
                                                panel de YouTube. Fuera del texto —la IA lo leía
                                                como si fuera parte de lo hablado— y encima del
                                                fragmento que abre, que es lo que señala. */}
                                            {seg.titulo && (
                                                <div className="ee-seg-capitulo">
                                                    <span className="ee-seg-capitulo-marca">▸</span>
                                                    {seg.titulo}
                                                    <span className="ee-seg-capitulo-nota">
                                                        rótulo de YouTube · no se dice en el vídeo
                                                    </span>
                                                </div>
                                            )}

                                            {/* Segment Text */}
                                            <p className="ee-seg-text" onClick={() => oyeFragmento(seg)}>
                                                {seg.text}
                                            </p>

                                            {/* Step 2: Precision time editor */}
                                            {step === 2 && openPrecision.has(seg.id) && (
                                                <SegmentTimePrecisionEditor
                                                    seg={seg}
                                                    prevSeg={idx > 0 ? segments[idx - 1] : null}
                                                    nextSeg={idx < segments.length - 1 ? segments[idx + 1] : null}
                                                    onUpdate={(changes, propagate) => updateSegmentTime(seg.id, changes, propagate)}
                                                    playerRef={playerRef}
                                                    videoDuration={videoDuration}
                                                />
                                            )}

                                            {/* Step 2: Exercise slots list + Add button */}
                                            {step === 2 && (
                                                <div className="ee-exercises-list">
                                                    {seg.exercises.map((ex, exIdx) => {
                                                        const exCfg = EXERCISE_TYPES[ex.type] ?? EXERCISE_TYPES.quiz;
                                                        return (
                                                            <div key={ex.id} className="ee-ex-slot">
                                                                <div className="ee-ex-slot-header">
                                                                    <span className="ee-ex-slot-num">Ejercicio {exIdx + 1}</span>
                                                                    <button
                                                                        className="ee-ex-slot-remove"
                                                                        onClick={() => removeExerciseFromSeg(seg.id, ex.id)}
                                                                        title="Eliminar ejercicio"
                                                                    >✕</button>
                                                                </div>
                                                                <div className="ee-type-row">
                                                                    {Object.entries(EXERCISE_TYPES).map(([key, cfg]) => (
                                                                        <button
                                                                            key={key}
                                                                            className={`ee-type-btn ${ex.type === key ? 'active' : ''}`}
                                                                            style={{ '--ec': cfg.color, '--edk': cfg.darkColor }}
                                                                            onClick={() => setExerciseType(seg.id, ex.id, key)}
                                                                            title={cfg.description}
                                                                        >
                                                                            {cfg.emoji} {cfg.label}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                    <button
                                                        className="ee-add-ex-btn"
                                                        onClick={() => addExerciseToSeg(seg.id)}
                                                    >
                                                        ＋ Añadir ejercicio
                                                    </button>
                                                </div>
                                            )}

                                            {/* Step 3: Per-exercise generate controls */}
                                            {step === 3 && (
                                                <div className="ee-exercises-list">
                                                    {seg.exercises.map((ex, exIdx) => {
                                                        const exCfg = EXERCISE_TYPES[ex.type] ?? EXERCISE_TYPES.quiz;
                                                        const isEditingThis = editingExKey?.segId === seg.id && editingExKey?.exId === ex.id;
                                                        const estaAbierto = ejerciciosAbiertos.has(`${seg.id}:${ex.id}`);
                                                        //  Sin nada escrito la fila está siempre a la vista:
                                                        //  es el momento en que hace falta —justo después de
                                                        //  «Añadir ejercicio»— y no hay nada que proteger.
                                                        const tiposALaVista = !ex.data || cambiandoTipo.has(`${seg.id}:${ex.id}`);
                                                        return (
                                                            <div key={ex.id} className="ee-ex-slot">
                                                                <div className="ee-seg-gen-row">
                                                                    <span className="ee-ex-slot-num">Ejercicio {exIdx + 1}</span>
                                                                    {/* La chapa del tipo abre y cierra la fila para
                                                                        elegir otro. Es el mismo sitio donde se lee
                                                                        qué es, que es donde se va a buscar. */}
                                                                    <button
                                                                        className="ee-type-badge ee-type-badge--boton"
                                                                        style={{ '--ec': exCfg.color, '--edk': exCfg.darkColor }}
                                                                        onClick={() => alternaCambioDeTipo(seg.id, ex.id)}
                                                                        disabled={ex.status === 'generating'}
                                                                        title="Cambiar el tipo de ejercicio"
                                                                    >
                                                                        {exCfg.emoji} {exCfg.label} <span className="ee-type-badge-flecha">{tiposALaVista ? '▴' : '▾'}</span>
                                                                    </button>
                                                                    {ex.status === 'generating' && <span className="ee-spin">⏳</span>}
                                                                    {ex.status === 'ready' && <span>✅</span>}
                                                                    {ex.status === 'error' && <span>❌</span>}
                                                                    <button
                                                                        className={`ee-gen-btn ${ex.status === 'ready' ? 'ready' : ''} ${ex.status === 'generating' ? 'loading' : ''}`}
                                                                        onClick={() => generateOneExercise(seg.id, ex.id)}
                                                                        disabled={ex.status === 'generating' || generatingAll}
                                                                    >
                                                                        {ex.status === 'ready' && '✓ Regenerar'}
                                                                        {ex.status === 'generating' && '⏳ Generando...'}
                                                                        {ex.status === 'pending' && '🤖 Generar'}
                                                                        {ex.status === 'error' && '🔄 Reintentar'}
                                                                    </button>
                                                                    {/* Sólo cuando hay algo que ver. Un «Ver» que
                                                                        abre un hueco enseña a no pulsarlo. */}
                                                                    {ex.data && (
                                                                        <button
                                                                            className={`ee-gen-btn ${estaAbierto ? 'ready' : ''}`}
                                                                            onClick={() => alternaVista(seg.id, ex.id)}
                                                                            title="Ver el ejercicio entero, sin poder tocarlo"
                                                                        >
                                                                            {estaAbierto ? '👁 Cerrar' : '👁 Ver'}
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        className={`ee-gen-btn ${isEditingThis ? 'ready' : ''}`}
                                                                        onClick={() => setEditingExKey(isEditingThis ? null : { segId: seg.id, exId: ex.id })}
                                                                        disabled={ex.status === 'generating'}
                                                                    >
                                                                        {ex.data ? '✏️ Editar' : '✏️ Manual'}
                                                                    </button>
                                                                    <button
                                                                        className="ee-ex-slot-remove"
                                                                        onClick={() => removeExerciseFromSeg(seg.id, ex.id)}
                                                                        title="Eliminar ejercicio"
                                                                    >✕</button>
                                                                </div>

                                                                {/* Elegir el tipo sin volver al paso 2 */}
                                                                {tiposALaVista && (
                                                                    <div className="ee-type-row ee-type-row--gen">
                                                                        {Object.entries(EXERCISE_TYPES).map(([key, cfg]) => (
                                                                            <button
                                                                                key={key}
                                                                                className={`ee-type-btn ${ex.type === key ? 'active' : ''}`}
                                                                                style={{ '--ec': cfg.color, '--edk': cfg.darkColor }}
                                                                                onClick={() => cambiaTipoEnElPaso3(seg, ex, key, idx + 1, exIdx + 1)}
                                                                                disabled={ex.status === 'generating'}
                                                                                title={cfg.description}
                                                                            >
                                                                                {cfg.emoji} {cfg.label}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                )}

                                                                {/* Manual editor for this exercise */}
                                                                {isEditingThis && (
                                                                    <ManualExerciseEditor
                                                                        exerciseType={ex.type}
                                                                        initialExercise={ex.data}
                                                                        defaultText={seg.text}
                                                                        onSave={(newData) => {
                                                                            setSegments(prev => prev.map(s => s.id !== seg.id ? s : {
                                                                                ...s,
                                                                                exercises: s.exercises.map(e =>
                                                                                    e.id === ex.id ? { ...e, data: newData, status: 'ready' } : e
                                                                                ),
                                                                            }));
                                                                            setEditingExKey(null);
                                                                            showToast(`✅ Ejercicio ${exIdx + 1} del segmento #${idx + 1} guardado`, 'success');
                                                                        }}
                                                                        onCancel={() => setEditingExKey(null)}
                                                                    />
                                                                )}

                                                                {/* Todo el ejercicio, para revisarlo */}
                                                                {ex.data && !isEditingThis && estaAbierto && (
                                                                    <VistaDelEjercicio
                                                                        data={ex.data}
                                                                        tipoPedido={ex.type}
                                                                        etiquetas={ETIQUETAS_DE_TIPO}
                                                                    />
                                                                )}

                                                                {/* Compact data preview */}
                                                                {ex.data && !isEditingThis && !estaAbierto && (
                                                                    <div className="ee-ex-compact">
                                                                        {ex.data.type === 'truefalse' && <p className="ee-ex-compact-text">📋 <em>{ex.data.statement?.slice(0, 80)}...</em></p>}
                                                                        {ex.data.type === 'quiz' && <p className="ee-ex-compact-text">❓ <em>{ex.data.question?.slice(0, 80)}...</em></p>}
                                                                        {ex.data.type === 'fill' && <p className="ee-ex-compact-text">✏️ <em>{ex.data.template?.slice(0, 80)}...</em></p>}
                                                                        {ex.data.type === 'wordselect' && <p className="ee-ex-compact-text">🔤 <em>{ex.data.targetWords?.join(', ')}</em></p>}
                                                                        {ex.data.type === 'match' && <p className="ee-ex-compact-text">🔗 <em>{ex.data.pairs?.map(p => p.left).join(', ')}</em></p>}
                                                                        {ex.data.type === 'dragdrop' && <p className="ee-ex-compact-text">🧩 <em>{ex.data.template?.slice(0, 80)}...</em></p>}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                    <button
                                                        className="ee-add-ex-btn"
                                                        onClick={() => addExerciseToSeg(seg.id)}
                                                    >
                                                        ＋ Añadir ejercicio
                                                    </button>
                                                </div>
                                            )}

                                            {/* Step 4: Full interactive preview of all exercises */}
                                            {step === 5 && (
                                                <div className="ee-seg-preview">
                                                    {seg.exercises.length === 0 && (
                                                        <div className="ee-no-exercise">Sin ejercicios — vuelve al paso 3</div>
                                                    )}
                                                    {seg.exercises.map((ex, exIdx) => {
                                                        const exCfg = EXERCISE_TYPES[ex.type] ?? EXERCISE_TYPES.quiz;
                                                        return (
                                                            <div key={ex.id} className="ee-preview-ex-item">
                                                                <div className="ee-type-badge small" style={{ '--ec': exCfg.color, '--edk': exCfg.darkColor }}>
                                                                    {exCfg.emoji} {exCfg.label} · Ejercicio {exIdx + 1}
                                                                </div>
                                                                {ex.data
                                                                    ? <ExercisePreview exercise={ex.data} segmentText={seg.text} />
                                                                    : <div className="ee-no-exercise">Sin datos — genera o crea manualmente</div>
                                                                }
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}